import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  where
} from 'firebase/firestore';
import {
  db,
  initAnalytics,
  registerWithEmail,
  sendPasswordReset,
  signInWithEmail,
  signInWithGoogle,
  completeGoogleRedirect,
  signOutUser,
  trackEvent,
  watchAuthState,
  VSCODE_EXTENSION_URL
} from './firebase';
import { wheniseeusAI, enviarConsultaSoporte, notificarRegistro, transcribeSpeech } from './api';
import { attachGoogleResults } from './googleSearch';
import { translate } from './i18n';
import LanguageToggle from './LanguageToggle';
import EyeBadge from './EyeBadge';
import LoadingStatus from './LoadingStatus';
import BrainPanel from './BrainPanel';
import RemotePinSettings from './RemotePinSettings';
import ApiKeysSettings from './ApiKeysSettings';
import { uploadAttachment, uploadBlob, captureVideoFrameFromFile, captureStreamFrame, isStorageUrl, isWebUrl } from './attachments';
import { micSupported, ttsSupported, openVoiceListener, speak, stopSpeaking, CURATED_VOICES } from './speech';
import { useTheme } from './theme';
import './App.css';

const HISTORY_PAGE_SIZE = 20;
const MAX_HISTORY_MESSAGES = 12;
// Per-turn cap of the backend's ChatHistoryMessage.text (schemas.py).
const MAX_HISTORY_TEXT = 4000;
// The most any plan allows (premium/coders) - kept in sync by hand with
// backend/app/schemas.py. The per-plan cap is maxImagesForTier below.
const MAX_IMAGE_ATTACHMENTS = 8;
const maxImagesForTier = (tier) => (tier === 'premium' || tier === 'coders' ? 8 : 4);
// Kept in sync by hand with backend/app/schemas.py's MAX_DOCUMENT_ATTACHMENTS.
const MAX_DOCUMENT_ATTACHMENTS = 3;
// What the backend can actually extract text from - see
// backend/app/services/documents.py (legacy .doc isn't supported).
const DOCUMENT_ACCEPT = '.pdf,.docx,.txt,application/pdf,text/plain,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const screenShareSupported = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getDisplayMedia;

const LOGO_SRC = '/logo192.png?v=20260911';

// Someone opening wheniseeus.com on a computer without a session sees the
// landing page (get.wheniseeus.com) instead of the login screen. Its
// "Iniciar sesión / Registrarse" link comes back with ?entrar=1, and its
// download buttons with ?instalar=1 (InstallPrompt.js) - both stay here for
// the rest of the tab's session. Decided once per page load, so in-app
// trips to / (from /subscribe, /workspaces...) still show the login screen.
// Phones and the installed app keep the login screen.
const LANDING_URL = 'https://get.wheniseeus.com/';
const STAY_ON_LOGIN_KEY = 'wheniseeus-entrar';
const sendToLandingWhenLoggedOut = (() => {
  if (typeof window === 'undefined' || window.location.pathname !== '/') return false;
  const params = new URLSearchParams(window.location.search);
  let stay = false;
  try {
    stay = sessionStorage.getItem(STAY_ON_LOGIN_KEY) === '1';
    if (['entrar', 'instalar', 'install'].some((p) => params.has(p))) {
      sessionStorage.setItem(STAY_ON_LOGIN_KEY, '1');
      stay = true;
    }
  } catch {
    stay = stay || ['entrar', 'instalar', 'install'].some((p) => params.has(p));
  }
  if (params.has('entrar')) {
    params.delete('entrar');
    const query = params.toString();
    window.history.replaceState(null, '', window.location.pathname + (query ? `?${query}` : '') + window.location.hash);
  }
  const desktop = window.matchMedia?.('(pointer: fine) and (min-width: 900px)').matches;
  const installed = window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone;
  return !stay && desktop && !installed;
})();

const createConversation = (id, lang) => ({
  id: String(id),
  title: translate(lang, 'newConversationTitle'),
  messages: [],
  updatedAt: Date.now()
});

const normalizeAttachments = (attachments) => {
  if (!Array.isArray(attachments)) {
    return [];
  }

  return attachments
    .filter((a) => a && isStorageUrl(a.url) && (a.type === 'image' || a.type === 'video'))
    .slice(0, MAX_IMAGE_ATTACHMENTS)
    .map((a) => ({ type: a.type, url: a.url, name: typeof a.name === 'string' ? a.name : '' }));
};

function useClickOutside(ref, enabled, setOpen) {
  useEffect(() => {
    if (!enabled) return;
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [ref, enabled, setOpen]);
}

const hostnameOf = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
};

const LONG_WAIT_NOTICE_DELAY_MS = 4 * 60 * 1000;

const normalizeMessages = (messages) => {
  if (!Array.isArray(messages) || messages.length === 0) {
    return [];
  }

  return messages.map((message) => {
    const attachments = normalizeAttachments(message?.attachments);
    return {
      role: message?.role === 'user' ? 'user' : 'ai',
      text: typeof message?.text === 'string' ? message.text : '',
      ...(Array.isArray(message?.fuentes) && message.fuentes.length > 0 ? { fuentes: message.fuentes } : {}),
      ...(attachments.length > 0 ? { attachments } : {})
    };
  });
};

const normalizeConversation = (conversation, lang) => ({
  id: String(conversation?.id || Date.now()),
  title: conversation?.title || translate(lang, 'newConversationTitle'),
  messages: normalizeMessages(conversation?.messages),
  updatedAt: typeof conversation?.updatedAt === 'number' ? conversation.updatedAt : Date.now()
});

const buildConversationTitle = (text, lang) => {
  const normalized = text.trim();
  if (!normalized) {
    return translate(lang, 'newConversationTitle');
  }

  return normalized.length > 28 ? `${normalized.slice(0, 28)}...` : normalized;
};

const DEFAULT_SETTINGS = { theme: 'light', fontSize: 'normal', language: 'en', readAloud: false, voiceURI: '' };

const hasMessages = (conversation) => conversation.messages.some((m) => m.text.trim() || m.attachments?.length);

const conversationToMarkdown = (conversation, lang) => {
  const lines = [`# ${conversation.title}`, '', `_${new Date(conversation.updatedAt).toLocaleString(lang)}_`, ''];
  conversation.messages.forEach((message) => {
    lines.push(`**${message.role === 'user' ? translate(lang, 'exportYou') : 'wheniseeus'}:**`, '', message.text);
    (message.attachments || []).forEach((a) => lines.push('', `- [${a.name || a.type}](${a.url})`));
    if (message.fuentes?.length) {
      lines.push('', `${translate(lang, 'sourcesLabel')}`);
      message.fuentes.forEach((f) => lines.push(`- [${f.title || hostnameOf(f.link) || f.link}](${f.link})`));
    }
    lines.push('', '---', '');
  });
  return lines.join('\n');
};

const downloadTextFile = (filename, content, mimeType) => {
  const url = URL.createObjectURL(new Blob([content], { type: `${mimeType};charset=utf-8` }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const exportFileStamp = () => new Date().toISOString().slice(0, 10);

const safeFileName = (text) => (text || 'conversation').replace(/[\\/:*?"<>|]+/g, '').replace(/\s+/g, '-').slice(0, 40) || 'conversation';

const ADMIN_EMAILS = (process.env.REACT_APP_ADMIN_EMAILS || '')
  .split(',')
  .map((email) => email.trim().toLowerCase())
  .filter(Boolean);

// Inline SVG icons
const Icons = {
  robot: (
    <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 24, height: 24 }}>
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm3.5-9c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11zm3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z"/>
    </svg>
  ),
  add: (
    <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 20, height: 20 }}>
      <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
    </svg>
  ),
  delete: (
    <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 20, height: 20 }}>
      <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-9l-1 1H5v2h14V4z"/>
    </svg>
  ),
  settings: (
    <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 20, height: 20 }}>
      <path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.62l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.48.1.62l2.03 1.58c-.05.3-.07.62-.07.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.62l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.48-.1-.62l-2.03-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"/>
    </svg>
  ),
  send: (
    <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 18, height: 18 }}>
      <path d="M3.4 20.4l17.45-7.48c.98-.42.98-1.82 0-2.24L3.4 3.2c-.88-.38-1.79.44-1.52 1.35l1.7 5.63a1 1 0 00.95.71h8.03l-8.03.86a1 1 0 00-.93.72l-1.72 6.53c-.24.9.66 1.69 1.52 1.32z"/>
    </svg>
  ),
  stop: (
    <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 14, height: 14 }}>
      <rect x="4" y="4" width="16" height="16" rx="2" />
    </svg>
  ),
  check: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
  document: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 20, height: 20 }}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
    </svg>
  ),
  user: (
    <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 20, height: 20 }}>
      <path d="M12 12c2.76 0 5-2.24 5-5S14.76 2 12 2 7 4.24 7 7s2.24 5 5 5zm0 2c-3.33 0-10 1.67-10 5v1h20v-1c0-3.33-6.67-5-10-5z" />
    </svg>
  ),
  history: (
    <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 20, height: 20 }}>
      <path d="M4 6h16v2H4V6zm0 5h16v2H4v-2zm0 5h10v2H4v-2z" />
    </svg>
  ),
  google: (
    <svg viewBox="0 0 24 24" aria-hidden="true" style={{ width: 18, height: 18 }}>
      <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.24 1.26-.97 2.33-2.05 3.05l3.32 2.58c1.94-1.79 3.06-4.43 3.06-7.58 0-.72-.06-1.42-.18-2.09H12z" />
      <path fill="#34A853" d="M12 22c2.7 0 4.97-.9 6.63-2.46l-3.32-2.58c-.92.62-2.1.99-3.31.99-2.55 0-4.71-1.72-5.48-4.03l-3.43 2.65A10.01 10.01 0 0012 22z" />
      <path fill="#4A90E2" d="M6.52 13.92A5.99 5.99 0 016.2 12c0-.67.11-1.31.32-1.92L3.1 7.43A10 10 0 002 12c0 1.61.39 3.14 1.1 4.57l3.42-2.65z" />
      <path fill="#FBBC05" d="M12 5.98c1.47 0 2.8.51 3.84 1.52l2.88-2.88C16.96 2.99 14.7 2 12 2A10.01 10.01 0 003.1 7.43l3.42 2.65C7.29 7.7 9.45 5.98 12 5.98z" />
    </svg>
  ),
  eye: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 18, height: 18 }}>
      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  eyeOff: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 18, height: 18 }}>
      <path d="M17.94 17.94A10.94 10.94 0 0112 19c-7 0-11-7-11-7a21.6 21.6 0 015.06-6.06M9.9 4.24A10.94 10.94 0 0112 4c7 0 11 7 11 7a21.6 21.6 0 01-3.22 4.4M14.12 14.12a3 3 0 11-4.24-4.24" />
      <path d="M1 1l22 22" />
    </svg>
  ),
  menu: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ width: 22, height: 22 }}>
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  ),
  close: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ width: 18, height: 18 }}>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  refresh: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 20, height: 20 }}>
      <path d="M21 12a9 9 0 1 1-2.64-6.36" />
      <polyline points="21 3 21 9 15 9" />
    </svg>
  ),
  clock: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 20, height: 20 }}>
      <circle cx="12" cy="12" r="9" />
      <polyline points="12 7 12 12 15.5 14" />
    </svg>
  ),
  support: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 20, height: 20 }}>
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 015.83 1c0 2-3 2-3 4" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
  chevronDown: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16 }}>
      <polyline points="6 9 12 15 18 9" />
    </svg>
  ),
  lightMode: (
    <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 18, height: 18 }}>
      <path d="M12 7c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5zM2 13h2c.55 0 1-.45 1-1s-.45-1-1-1H2c-.55 0-1 .45-1 1s.45 1 1 1zm18 0h2c.55 0 1-.45 1-1s-.45-1-1-1h-2c-.55 0-1 .45-1 1s.45 1 1 1zM11 2v2c0 .55.45 1 1 1s1-.45 1-1V2c0-.55-.45-1-1-1s-1 .45-1 1zm0 18v2c0 .55.45 1 1 1s1-.45 1-1v-2c0-.55-.45-1-1-1s-1 .45-1 1zM5.99 4.58c-.39-.39-1.03-.39-1.41 0-.39.39-.39 1.03 0 1.41l1.06 1.06c.39.39 1.03.39 1.41 0s.39-1.03 0-1.41L5.99 4.58zm12.37 12.37c-.39-.39-1.03-.39-1.41 0-.39.39-.39 1.03 0 1.41l1.06 1.06c.39.39 1.03.39 1.41 0 .39-.39.39-1.03 0-1.41l-1.06-1.06zm1.06-10.96c.39-.39.39-1.03 0-1.41-.39-.39-1.03-.39-1.41 0l-1.06 1.06c-.39.39-.39 1.03 0 1.41s1.03.39 1.41 0l1.06-1.06zM7.05 18.36c.39-.39.39-1.03 0-1.41-.39-.39-1.03-.39-1.41 0l-1.06 1.06c-.39.39-.39 1.03 0 1.41s1.03.39 1.41 0l1.06-1.06z" />
    </svg>
  ),
  darkMode: (
    <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 18, height: 18 }}>
      <path d="M12 3c-4.97 0-9 4.03-9 9s4.03 9 9 9 9-4.03 9-9c0-.46-.04-.92-.1-1.36-.98 1.37-2.58 2.26-4.4 2.26-2.98 0-5.4-2.42-5.4-5.4 0-1.81.89-3.42 2.26-4.4-.44-.06-.9-.1-1.36-.1z" />
    </svg>
  ),
  autoMode: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 18, height: 18 }}>
      <rect x="3" y="4" width="18" height="13" rx="2" />
      <path d="M8 21h8M12 17v4" />
      <path d="M12 8a2.5 2.5 0 1 1 0 5" fill="currentColor" stroke="none" />
    </svg>
  ),
  mic: (
    <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 18, height: 18 }}>
      <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5-3c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
    </svg>
  ),
  attach: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 18, height: 18 }}>
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
    </svg>
  ),
  image: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 18, height: 18 }}>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <path d="M21 15l-5-5L5 21" />
    </svg>
  ),
  video: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 18, height: 18 }}>
      <rect x="2" y="6" width="14" height="12" rx="2" />
      <path d="M22 8.5l-6 3.5 6 3.5v-7z" />
    </svg>
  ),
  screenShare: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 18, height: 18 }}>
      <rect x="2" y="4" width="20" height="14" rx="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
      <polyline points="9 10 12 7 15 10" />
      <line x1="12" y1="7" x2="12" y2="14" />
    </svg>
  ),
  speaker: (
    <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 16, height: 16 }}>
      <path d="M5 9v6h4l5 5V4L9 9H5zm11.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z" />
    </svg>
  ),
  copySmall: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ width: 15, height: 15 }}>
      <rect x="9" y="9" width="13" height="13" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  ),
  thumbUp: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ width: 15, height: 15 }}>
      <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z" />
      <path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
    </svg>
  ),
  thumbDown: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ width: 15, height: 15 }}>
      <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3H10z" />
      <path d="M17 2h3a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2h-3" />
    </svg>
  ),
  share: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ width: 15, height: 15 }}>
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
    </svg>
  ),
  code: (
    <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 20, height: 20 }}>
      <path d="M9.4 16.6 4.8 12l4.6-4.6L8 6l-6 6 6 6zm5.2 0 4.6-4.6-4.6-4.6L16 6l6 6-6 6z" />
    </svg>
  ),
  team: (
    <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 20, height: 20 }}>
      <path d="M12 12c1.93 0 3.5-1.57 3.5-3.5S13.93 5 12 5 8.5 6.57 8.5 8.5 10.07 12 12 12zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4zm7.5-6c1.1 0 2-.9 2-2s-.9-2-2-2c-.35 0-.68.1-.96.26.6.78.96 1.75.96 2.74s-.36 1.96-.96 2.74c.28.16.61.26.96.26zm2.5 4.5c0-.83-.49-1.55-1.2-1.88.93.68 1.7 1.72 1.7 3.13V16h2v-1.25c0-1.4-1.62-2.4-2.5-2.75zM4.5 10c.35 0 .68-.1.96-.26A4.49 4.49 0 014.5 7c0-.99.36-1.96.96-2.74A2 2 0 004.5 4a2 2 0 000 4 2 2 0 000 2zm-.5 2.75C3.12 13.1 1.5 14.1 1.5 15.5V16h2v-1.87c0-1.41.77-2.45 1.7-3.13A2.18 2.18 0 003.9 12z" />
    </svg>
  )
};

// Languages that get promoted to a full artifact card + side panel (like
// Claude's Artifacts) instead of rendering inline - these are the ones
// where a live preview is meaningful and self-contained per block.
const ARTIFACT_CARD_LANGS = new Set(['jsx', 'tsx', 'react', 'ts', 'typescript']);

// Fallback for fenced blocks the model left untagged (plain ``` with no
// "html"/"css" after it) - without this, an untagged HTML or CSS block
// silently fails to merge into the page preview below.
function sniffLanguage(code) {
  const trimmed = code.trim();
  if (/^<!doctype\s+html/i.test(trimmed) || /^<html[\s>]/i.test(trimmed)) return 'html';
  if (!trimmed.includes('<') && /[.#]?[\w-]+\s*\{[^{}]*:[^{}]*\}/.test(trimmed)) return 'css';
  return '';
}

function extractCodeBlocks(text) {
  const regex = /```(\w+)?\n([\s\S]*?)```/g;
  const blocks = [];
  let match;
  while ((match = regex.exec(text)) !== null) {
    const language = (match[1] || '').toLowerCase() || sniffLanguage(match[2]);
    blocks.push({ language, code: match[2] });
  }
  return blocks;
}

// Best-effort live preview: HTML renders directly, JS/JSX/TS gets stripped
// of import/export (no module system in this sandboxed iframe) and
// rendered via React + Babel standalone from a CDN. Can't handle real npm
// imports or multi-file projects - meant for the self-contained snippets
// the model actually produces in chat.
function buildPreviewDoc(code, language) {
  if (language === 'html' || language === 'htm') {
    return code;
  }

  const cleaned = code
    .replace(/^\s*import\s+[^;]+;?\s*$/gm, '')
    .replace(/export\s+default\s+/g, '')
    .replace(/export\s+/g, '');

  const nameMatch = cleaned.match(/(?:function|const|class)\s+([A-Z]\w*)/);
  const componentName = nameMatch ? nameMatch[1] : null;

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<style>
  body { margin: 0; padding: 16px; font-family: -apple-system, BlinkMacSystemFont, sans-serif; color: #1c1917; }
  #preview-error { color: #dc2626; white-space: pre-wrap; font-family: monospace; font-size: 12px; }
</style>
</head>
<body>
<div id="root"></div>
<div id="preview-error"></div>
<script src="https://unpkg.com/react@18/umd/react.production.min.js"></script>
<script src="https://unpkg.com/react-dom@18/umd/react-dom.production.min.js"></script>
<script src="https://unpkg.com/@babel/standalone/babel.min.js"></script>
<script type="text/babel" data-presets="react,typescript" data-type="module">
const { useState, useEffect, useRef, useMemo, useCallback, useReducer } = React;
try {
  ${cleaned}
  const Component = ${componentName || 'null'};
  if (Component) {
    ReactDOM.createRoot(document.getElementById('root')).render(React.createElement(Component));
  } else {
    document.getElementById('preview-error').textContent = 'No se encontró un componente para previsualizar.';
  }
} catch (err) {
  document.getElementById('preview-error').textContent = String(err && err.message ? err.message : err);
}
</script>
</body>
</html>`;
}

// The model often splits a "build me a page" answer into separate HTML,
// CSS and JS fenced blocks rather than one self-contained file. This
// assembles one combined document (CSS injected into <head>, JS injected
// before </body>) so there's a single working preview for the whole page.
function buildCombinedPreviewDoc(blocks) {
  const htmlBlock = blocks.find((b) => b.language === 'html' || b.language === 'htm');
  if (!htmlBlock) return null;

  const cssBlocks = blocks.filter((b) => b.language === 'css');
  const jsBlocks = blocks.filter((b) => b.language === 'js' || b.language === 'javascript');

  let doc = htmlBlock.code;

  if (cssBlocks.length > 0) {
    const styleTag = `<style>${cssBlocks.map((b) => b.code).join('\n')}</style>`;
    doc = /<\/head>/i.test(doc) ? doc.replace(/<\/head>/i, `${styleTag}</head>`) : styleTag + doc;
  }

  if (jsBlocks.length > 0) {
    const scriptTag = `<script>${jsBlocks.map((b) => b.code).join('\n')}</script>`;
    doc = /<\/body>/i.test(doc) ? doc.replace(/<\/body>/i, `${scriptTag}</body>`) : doc + scriptTag;
  }

  return doc;
}

// One artifact per message, at most: an HTML/CSS/JS combo (or a lone HTML
// block) wins over everything else, since that's the "page" the model was
// probably building; otherwise a lone standalone JS block still qualifies.
function detectPageArtifact(text, uiLang) {
  const blocks = extractCodeBlocks(text);
  const htmlBlock = blocks.find((b) => b.language === 'html' || b.language === 'htm');

  if (htmlBlock) {
    return {
      code: htmlBlock.code,
      language: 'html',
      doc: buildCombinedPreviewDoc(blocks),
      title: uiLang === 'es' ? 'Página web' : 'Web page'
    };
  }

  const jsBlock = blocks.find((b) => b.language === 'js' || b.language === 'javascript');
  if (jsBlock) {
    return { code: jsBlock.code, language: 'javascript', doc: null, title: 'JavaScript' };
  }

  return null;
}

function ArtifactCard({ title, subtitle, onOpen }) {
  return (
    <button type="button" className="artifact-card" onClick={onOpen}>
      <span className="artifact-card-icon">{Icons.attach}</span>
      <span className="artifact-card-text">
        <strong>{title}</strong>
        <small>{subtitle}</small>
      </span>
    </button>
  );
}

function CopyButton({ text, uiLang, className }) {
  const [copied, setCopied] = useState(false);
  const resetTimerRef = useRef(null);

  useEffect(() => () => clearTimeout(resetTimerRef.current), []);

  const handleClick = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    clearTimeout(resetTimerRef.current);
    resetTimerRef.current = setTimeout(() => setCopied(false), 1800);
  };

  return (
    <button
      className={`${className}${copied ? ' copied' : ''}`}
      type="button"
      onClick={handleClick}
    >
      {copied ? Icons.check : null}
      {translate(uiLang, copied ? 'copied' : 'copyCode')}
    </button>
  );
}

function MessageActionsRow({ text, uiLang, showSpeak, onSpeak, canRetry, onRetry, feedback, onFeedback }) {
  const [copied, setCopied] = useState(false);
  const copyTimerRef = useRef(null);

  useEffect(() => () => clearTimeout(copyTimerRef.current), []);

  const flashCopied = () => {
    setCopied(true);
    clearTimeout(copyTimerRef.current);
    copyTimerRef.current = setTimeout(() => setCopied(false), 1800);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    flashCopied();
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ text });
      } catch (shareError) {
        // Share sheet dismissed by the user - nothing to do.
      }
    } else {
      navigator.clipboard.writeText(text);
      flashCopied();
    }
  };

  return (
    <div className="message-actions">
      {showSpeak && (
        <button
          type="button"
          className="message-action-btn"
          onClick={onSpeak}
          aria-label={translate(uiLang, 'speakMessageAria')}
          title={translate(uiLang, 'speakMessageAria')}
        >
          {Icons.speaker}
        </button>
      )}
      <button
        type="button"
        className="message-action-btn"
        onClick={handleCopy}
        aria-label={translate(uiLang, 'copyMessageAria')}
        title={translate(uiLang, 'copyMessageAria')}
      >
        {copied ? Icons.check : Icons.copySmall}
      </button>
      {canRetry && (
        <button
          type="button"
          className="message-action-btn"
          onClick={onRetry}
          aria-label={translate(uiLang, 'retryMessageAria')}
          title={translate(uiLang, 'retryMessageAria')}
        >
          {Icons.refresh}
        </button>
      )}
      <button
        type="button"
        className={`message-action-btn${feedback === 'up' ? ' active' : ''}`}
        onClick={() => onFeedback('up')}
        aria-label={translate(uiLang, 'likeMessageAria')}
        title={translate(uiLang, 'likeMessageAria')}
        aria-pressed={feedback === 'up'}
      >
        {Icons.thumbUp}
      </button>
      <button
        type="button"
        className={`message-action-btn${feedback === 'down' ? ' active' : ''}`}
        onClick={() => onFeedback('down')}
        aria-label={translate(uiLang, 'dislikeMessageAria')}
        title={translate(uiLang, 'dislikeMessageAria')}
        aria-pressed={feedback === 'down'}
      >
        {Icons.thumbDown}
      </button>
      <button
        type="button"
        className="message-action-btn"
        onClick={handleShare}
        aria-label={translate(uiLang, 'shareMessageAria')}
        title={translate(uiLang, 'shareMessageAria')}
      >
        {Icons.share}
      </button>
    </div>
  );
}

function CodeBlock({ language, code, uiLang, onOpenArtifact }) {
  const lang = (language || '').toLowerCase();

  if (ARTIFACT_CARD_LANGS.has(lang)) {
    return (
      <ArtifactCard
        title={lang.toUpperCase()}
        subtitle={translate(uiLang, 'clickToView')}
        onOpen={() => onOpenArtifact({
          code,
          language: lang,
          doc: buildPreviewDoc(code, lang),
          title: `${lang.toUpperCase()} ${uiLang === 'es' ? 'componente' : 'component'}`
        })}
      />
    );
  }

  return (
    <div className="code-block-wrapper">
      <div className="code-block-header">
        <span className="code-lang">{language}</span>
        <CopyButton text={code} uiLang={uiLang} className="code-copy-btn" />
      </div>
      <SyntaxHighlighter
        style={oneDark}
        language={language}
        PreTag="div"
        customStyle={{ margin: 0, borderRadius: '0 0 10px 10px', fontSize: '13px' }}
      >
        {code}
      </SyntaxHighlighter>
    </div>
  );
}

// Google's own results box for the latest web search (see googleSearch.js) -
// Google's terms ask for the search element's results to be shown.
function GoogleResults() {
  const ref = useRef(null);
  useEffect(() => attachGoogleResults(ref.current), []);
  return <div ref={ref} className="google-results" />;
}

function AiMessageArtifactCard({ text, uiLang, onOpenArtifact }) {
  const artifactData = detectPageArtifact(text, uiLang);
  if (!artifactData) return null;

  return (
    <ArtifactCard
      title={artifactData.title}
      subtitle={translate(uiLang, 'clickToView')}
      onOpen={() => onOpenArtifact(artifactData)}
    />
  );
}

function ArtifactPanel({ artifact, tab, onTabChange, onClose, uiLang }) {
  if (!artifact) return null;

  return (
    <aside className="artifact-panel">
      <div className="artifact-panel-header">
        <span className="artifact-panel-title">{artifact.title}</span>
        <div className="artifact-panel-tabs">
          {artifact.doc && (
            <button
              type="button"
              className={`artifact-tab-btn${tab === 'preview' ? ' active' : ''}`}
              onClick={() => onTabChange('preview')}
            >
              {translate(uiLang, 'showPreview')}
            </button>
          )}
          <button
            type="button"
            className={`artifact-tab-btn${tab === 'code' ? ' active' : ''}`}
            onClick={() => onTabChange('code')}
          >
            {uiLang === 'es' ? 'Código' : 'Code'}
          </button>
        </div>
        <button
          type="button"
          className="artifact-panel-close"
          onClick={onClose}
          aria-label={translate(uiLang, 'close')}
        >
          {Icons.close}
        </button>
      </div>
      <div className="artifact-panel-body">
        {tab === 'preview' && artifact.doc ? (
          <iframe
            className="artifact-panel-frame"
            title="artifact-preview"
            sandbox="allow-scripts"
            srcDoc={artifact.doc}
          />
        ) : (
          <div className="artifact-panel-code">
            <CopyButton text={artifact.code} uiLang={uiLang} className="code-copy-btn artifact-code-copy" />
            <SyntaxHighlighter
              style={oneDark}
              language={artifact.language}
              customStyle={{ margin: 0, fontSize: '13px', height: '100%' }}
            >
              {artifact.code}
            </SyntaxHighlighter>
          </div>
        )}
      </div>
    </aside>
  );
}

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [settings, setSettings] = useState(() => {
    try {
      const saved = localStorage.getItem('northai-settings');
      return { ...DEFAULT_SETTINGS, ...(saved ? JSON.parse(saved) : {}) };
    } catch {
      return { ...DEFAULT_SETTINGS };
    }
  });
  const t = (key) => translate(settings.language, key);
  const [input, setInput] = useState("");
  const [conversations, setConversations] = useState([createConversation(1, settings.language)]);
  const [activeConversationId, setActiveConversationId] = useState(1);
  const [loading, setLoading] = useState(false);
  // Which conversation the in-flight request belongs to, so the loading
  // indicator only shows up on that conversation and not every other one
  // the user happens to be looking at while it's still generating.
  const [loadingConversationId, setLoadingConversationId] = useState(null);
  const [assistantStatus, setAssistantStatus] = useState('thinking'); // 'thinking' | 'searching' | 'sources' | 'designing'
  const [loadingSources, setLoadingSources] = useState([]);
  // The WISU combination the backend picked for a page/site/app request
  // ({estilo, paleta, estructura}), shown under the loader's status line.
  const [loadingDesign, setLoadingDesign] = useState(null);
  const [showLongWaitNotice, setShowLongWaitNotice] = useState(false);
  // The "brain" panel under the loader: Luruz's tokens and candidates live
  // (see BrainPanel.js). The stream fills brainStore without re-rendering.
  const brainStore = useRef({ modelo: null, tokens: [] });
  const [brainOpen, setBrainOpen] = useState(() => {
    try { return localStorage.getItem('wheniseeus-brain') === '1'; } catch { return false; }
  });
  const toggleBrain = () => setBrainOpen((open) => {
    try { localStorage.setItem('wheniseeus-brain', open ? '0' : '1'); } catch { /* per-browser convenience only */ }
    return !open;
  });
  const [showSettings, setShowSettings] = useState(false);
  // Remote VS Code's "Configurar PIN" link lands here (state.settings === 'pin').
  useEffect(() => {
    if (location.state?.settings !== 'pin') return;
    setShowSettings(true);
    navigate('.', { replace: true, state: null });
    setTimeout(() => document.getElementById('settings-remote-pin')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showSupport, setShowSupport] = useState(false);
  const [supportForm, setSupportForm] = useState({ nombre: '', email: '', mensaje: '' });
  const [supportSending, setSupportSending] = useState(false);
  const [supportSent, setSupportSent] = useState(false);
  const [supportError, setSupportError] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [authForm, setAuthForm] = useState({ email: '', password: '', confirmPassword: '' });
  const [authError, setAuthError] = useState('');
  const [authInfo, setAuthInfo] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [user, setUser] = useState(null);
  const [authResolved, setAuthResolved] = useState(false);
  const [subscribed, setSubscribed] = useState(null);
  const [tier, setTier] = useState('none');
  const maxImages = maxImagesForTier(tier);
  const [isAdmin, setIsAdmin] = useState(false);
  const [historyPanelOpen, setHistoryPanelOpen] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [micActive, setMicActive] = useState(false);
  const [voiceModeActive, setVoiceModeActive] = useState(false);
  const [aiSpeaking, setAiSpeaking] = useState(false);
  const [voiceTranscribing, setVoiceTranscribing] = useState(false);
  const [voiceNotice, setVoiceNotice] = useState('');
  const [micLevel, setMicLevel] = useState(0);
  const [speakLevel, setSpeakLevel] = useState(0);
  const [screenShareActive, setScreenShareActive] = useState(false);
  const chatEndRef = useRef(null);
  const pullStartYRef = useRef(null);
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshingPull, setRefreshingPull] = useState(false);
  const isHydratingHistoryRef = useRef(false);
  const voiceListenerRef = useRef(null);
  // Latest sendMessage - the voice listener's callbacks outlive the render
  // that created them.
  const sendMessageRef = useRef(null);
  const voiceModeActiveRef = useRef(false);
  const aiSpeakingRef = useRef(false);
  const abortControllerRef = useRef(null);
  const screenStreamRef = useRef(null);
  const screenVideoRef = useRef(null);
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const documentInputRef = useRef(null);
  const [messageFeedback, setMessageFeedback] = useState({});
  const attachMenuRef = useRef(null);
  const [attachMenuOpen, setAttachMenuOpen] = useState(false);
  const voiceMenuRef = useRef(null);
  const [voiceMenuOpen, setVoiceMenuOpen] = useState(false);
  const activeConversation = conversations.find((conversation) => conversation.id === activeConversationId) || conversations[0];
  const orderedConversations = [...conversations].sort((left, right) => right.updatedAt - left.updatedAt);
  const [historyVisibleCount, setHistoryVisibleCount] = useState(HISTORY_PAGE_SIZE);
  const visibleConversations = orderedConversations.slice(0, historyVisibleCount);
  const handleHistoryScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) {
      setHistoryVisibleCount((prev) => Math.min(prev + HISTORY_PAGE_SIZE, orderedConversations.length));
    }
  };
  const chat = useMemo(
    () => (activeConversation ? activeConversation.messages : []),
    [activeConversation]
  );
  // Only the latest search's results exist (one Google results element for
  // the whole app - see googleSearch.js), so only that reply shows them.
  const lastGoogleSearchIndex = chat.findLastIndex((msg) => msg.googleResultados);
  // Shared across the auth-loading, login, and post-login screens (see the
  // pull-to-refresh effect above) so the gesture gives the same visual
  // feedback no matter which one is showing when it fires.
  const pullToRefreshIndicator = (pullDistance > 0 || refreshingPull) ? (
    <div
      className={`pull-to-refresh-indicator${refreshingPull ? ' spinning' : ''}`}
      style={{
        opacity: Math.min(pullDistance / 70, 1),
        transform: `translateY(${Math.min(pullDistance, 70) - 40}px) rotate(${pullDistance * 2.5}deg)`
      }}
    >
      {Icons.refresh}
    </div>
  ) : null;
  const selectedVoiceName = settings.voiceURI
    ? (CURATED_VOICES[settings.language] || CURATED_VOICES.en).find((v) => v.voiceURI === settings.voiceURI)?.name
      || t('voiceAuto')
    : t('voiceAuto');

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chat]);

  // Past LONG_WAIT_NOTICE_DELAY_MS the wait is unusual enough (a cold
  // model load, or just slow CPU generation) to say so once, rather than
  // leave the bare dots up with no acknowledgment at all.
  useEffect(() => {
    if (!loading) {
      setShowLongWaitNotice(false);
      return;
    }
    const timer = setTimeout(() => setShowLongWaitNotice(true), LONG_WAIT_NOTICE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [loading]);

  // Custom pull-to-refresh: the app shell is a fixed-height layout
  // (body/.app-container have overflow: hidden, see App.css) so the
  // document itself never scrolls - the browser's native pull-to-refresh
  // gesture only fires off overscrolling the document, so it never
  // triggers here no matter how the user pulls on the inner scrollable
  // .chat-window. This re-implements that gesture manually on top of it.
  //
  // Bound to document.body rather than a ref to any one screen's root
  // element: the auth-loading screen, the login/register screen, and the
  // post-login app shell are three separate top-level returns from this
  // component, mounted and unmounted as auth state resolves, so a ref to
  // any single one of them would be null whenever a different screen is
  // showing (this is exactly what silently broke the gesture on the login
  // screen before - the listeners were only ever attached to the
  // post-login shell). body always exists, so binding here means the
  // gesture works identically on every screen with no auth-state
  // dependency to keep in sync.
  //
  // getScrollTop is target-aware: a touch landing inside ANY scrollable
  // region (chat history, a drawer's list, a dropdown menu, the settings
  // screen, the login form on a short viewport, ...) respects THAT
  // region's own scroll position, walking up from the touch target to the
  // nearest scrolled-and-scrollable ancestor - so dragging down mid-list
  // anywhere in the app still just scrolls it, rather than hijacking every
  // scrollable surface into a refresh gesture the moment it's not at its
  // own top. A touch that isn't inside any scrollable region (header,
  // input area, background padding) is always eligible, the same as the
  // header always was.
  //
  // Native (not React synthetic) listeners with { passive: false } are
  // required so preventDefault actually suppresses the browser's own
  // rubber-band scroll / native pull-to-refresh while dragging past the
  // top - on touchstart too, not just touchmove, so the browser commits
  // to letting JS own the gesture from the first touch instead of racing
  // its own gesture recognizer against our first touchmove (that race is
  // why the gesture used to work intermittently - once, then not the next
  // few times - rather than being reliably broken or reliably working).
  useEffect(() => {
    const el = document.body;

    const PULL_THRESHOLD = 70;
    const MAX_PULL = 110;
    const INTERACTIVE_SELECTOR = 'button, a, input, select, textarea, [role="button"]';

    const isScrollable = (node) => {
      const style = window.getComputedStyle(node);
      return (style.overflowY === 'auto' || style.overflowY === 'scroll')
        && node.scrollHeight > node.clientHeight;
    };

    const findScrollable = (target) => {
      let node = target instanceof Element ? target : target?.parentElement;
      while (node && node !== el) {
        if (isScrollable(node)) return node;
        node = node.parentElement;
      }
      return null;
    };

    // The scrollable ancestor (if any) found at the start of the current
    // gesture, and the touch Y from the previous move - together these let
    // touchmove manually replay the scroll it owes that element (see below).
    let scrollTarget = null;
    let lastY = null;

    const handleTouchStart = (e) => {
      // Don't hijack a tap on a button/link/etc. - calling preventDefault
      // here would suppress the browser's synthesized click event for it,
      // silently breaking buttons throughout the header, input area, and
      // messages (this is exactly what happened before this check existed).
      if (e.target.closest(INTERACTIVE_SELECTOR)) return;
      // A drawer (history/menu) or modal is a self-contained overlay with
      // its own scrollable region - claiming the gesture here for
      // pull-to-refresh would (a) fight the drawer's native touch scroll,
      // which is exactly what made history-list unscrollable on touch, and
      // (b) reload the whole page out from under an open drawer/modal on a
      // stray downward drag. Let the browser handle touch scrolling inside
      // these entirely; the gesture only applies to the main app surface.
      if (e.target.closest('.side-drawer, .modal')) return;
      scrollTarget = findScrollable(e.target);
      const scrollTop = scrollTarget ? scrollTarget.scrollTop : 0;
      if (scrollTop <= 0) {
        pullStartYRef.current = e.touches[0].clientY;
        lastY = e.touches[0].clientY;
        e.preventDefault();
      }
    };

    const handleTouchMove = (e) => {
      if (pullStartYRef.current === null) return;
      const currentY = e.touches[0].clientY;
      const delta = currentY - pullStartYRef.current;
      if (delta <= 0) {
        // Not a pull-down - the touch started at the top of scrollTarget
        // (or nowhere scrollable) and the finger is moving the other way,
        // i.e. trying to scroll further INTO the content below. touchstart
        // already called preventDefault to reliably claim this gesture (see
        // the comment above), which also suppresses the browser's own
        // scrolling for the rest of it - so without this, the very first
        // scroll attempt from the top of any list (most commonly: opening
        // the history drawer and immediately trying to scroll it) would be
        // silently swallowed. Replay it by hand instead.
        if (scrollTarget) {
          scrollTarget.scrollTop += lastY - currentY;
        }
        lastY = currentY;
        setPullDistance(0);
        return;
      }
      e.preventDefault();
      lastY = currentY;
      setPullDistance(Math.min(delta * 0.5, MAX_PULL));
    };

    const handleTouchEnd = () => {
      if (pullStartYRef.current === null) return;
      pullStartYRef.current = null;
      scrollTarget = null;
      lastY = null;
      setPullDistance((current) => {
        if (current >= PULL_THRESHOLD) {
          setRefreshingPull(true);
          window.location.reload();
        }
        return 0;
      });
    };

    el.addEventListener('touchstart', handleTouchStart, { passive: false });
    el.addEventListener('touchmove', handleTouchMove, { passive: false });
    el.addEventListener('touchend', handleTouchEnd, { passive: true });
    el.addEventListener('touchcancel', handleTouchEnd, { passive: true });

    return () => {
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
      el.removeEventListener('touchend', handleTouchEnd);
      el.removeEventListener('touchcancel', handleTouchEnd);
    };
    // Empty deps: body never unmounts, so this attaches once for the whole
    // app lifetime. getScrollTop walks the live DOM on every touch rather
    // than reading anything captured here, so it always reflects whichever
    // screen (or scroll position) is currently showing.
  }, []);

  useEffect(() => {
    initAnalytics();
  }, []);

  useEffect(() => {
    const unsubscribe = watchAuthState(async (nextUser) => {
      setUser(nextUser);
      setAuthResolved(true);
      if (nextUser) {
        try {
          // A one-shot read right as onAuthStateChanged fires can race
          // Firestore's own credential provider - the ID token isn't always
          // attached yet for the very first request, unlike a listener
          // (onSnapshot), which the SDK retries on its own once the token
          // catches up a moment later. This has no listener to fall back
          // on, so a single retry after a brief wait covers the same gap
          // instead of silently landing on the catch branch's defaults.
          let userDoc;
          try {
            userDoc = await getDoc(doc(db, 'users', nextUser.uid));
          } catch (raceError) {
            if (raceError?.code !== 'permission-denied') throw raceError;
            await new Promise((resolve) => setTimeout(resolve, 400));
            userDoc = await getDoc(doc(db, 'users', nextUser.uid));
          }
          // No profile doc yet means the signup call never landed (Google
          // sign-ins slipped past it) - the backend creates it, idempotently.
          if (!userDoc.exists()) notificarRegistro().catch(() => {});
          const data = userDoc.exists() ? userDoc.data() : {};
          const emailIsAdmin = !!nextUser.email && ADMIN_EMAILS.includes(nextUser.email.toLowerCase());
          const adminAccess = emailIsAdmin || data.isAdmin === true;
          setIsAdmin(adminAccess);
          setSubscribed(adminAccess || data.subscribed === true);
          setTier(adminAccess ? 'premium' : (data.tier || (data.subscribed ? 'pro' : 'none')));
        } catch {
          const emailIsAdmin = !!nextUser.email && ADMIN_EMAILS.includes(nextUser.email.toLowerCase());
          setIsAdmin(emailIsAdmin);
          if (emailIsAdmin) {
            setSubscribed(true);
            setTier('premium');
            return;
          }
          setSubscribed(false);
          setTier('none');
        }
      } else {
        setSubscribed(null);
        setIsAdmin(false);
        setTier('none');
      }
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    if (authResolved && user && !isAdmin && subscribed === false) {
      navigate('/subscribe');
    }
  }, [authResolved, isAdmin, user, subscribed, navigate]);

  useEffect(() => {
    try {
      localStorage.setItem('northai-settings', JSON.stringify(settings));
    } catch {}
    document.documentElement.setAttribute('data-fontsize', settings.fontSize);
  }, [settings]);

  const goingToLanding = authResolved && !user && sendToLandingWhenLoggedOut;
  useEffect(() => {
    if (goingToLanding) window.location.replace(LANDING_URL);
  }, [goingToLanding]);

  // The loading and login screens are always dark (#111214 / #000000 in App.css).
  useTheme(settings.theme, !authResolved ? '#111214' : !user ? '#000000' : null);

  const updateSetting = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleExportAll = (format) => {
    const exportable = conversations.filter(hasMessages);
    if (exportable.length === 0) {
      window.alert(t('exportNothing'));
      return;
    }
    const stamp = exportFileStamp();
    if (format === 'json') {
      const payload = { app: 'wheniseeus', exportedAt: new Date().toISOString(), conversations: exportable };
      downloadTextFile(`wheniseeus-conversations-${stamp}.json`, JSON.stringify(payload, null, 2), 'application/json');
    } else {
      const markdown = exportable.map((c) => conversationToMarkdown(c, settings.language)).join('\n\n');
      downloadTextFile(`wheniseeus-conversations-${stamp}.md`, markdown, 'text/markdown');
    }
  };

  const handleExportCurrent = () => {
    const current = conversations.find((c) => c.id === String(activeConversationId));
    if (!current || !hasMessages(current)) {
      window.alert(t('exportNothing'));
      return;
    }
    downloadTextFile(
      `wheniseeus-${safeFileName(current.title)}-${exportFileStamp()}.md`,
      conversationToMarkdown(current, settings.language),
      'text/markdown'
    );
  };

  const handleResetSettings = () => {
    if (!window.confirm(t('confirmResetSettings'))) return;
    setSettings((prev) => ({ ...DEFAULT_SETTINGS, language: prev.language }));
  };

  const handleClearAllHistory = async () => {
    if (!window.confirm(t('confirmDeleteHistory'))) return;
    if (user?.uid) {
      try {
        const chatsQuery = query(collection(db, 'chats'), where('userId', '==', user.uid));
        const snapshot = await getDocs(chatsQuery);
        // A workspace's group chat doc (workspaceId set) belongs to the
        // workspace, not this user's history - only the server deletes it.
        await Promise.all(snapshot.docs
          .filter((d) => !d.data().workspaceId)
          .map((d) => deleteDoc(doc(db, 'chats', d.id))));
      } catch (error) {
        console.error(error);
      }
    }
    const newConv = createConversation(String(Date.now()), settings.language);
    setConversations([newConv]);
    setActiveConversationId(newConv.id);
    setShowSettings(false);
  };

  useEffect(() => {
    let cancelled = false;

    if (!user?.uid) {
      isHydratingHistoryRef.current = false;
      const starterConversation = createConversation(Date.now(), settings.language);
      setConversations([starterConversation]);
      setActiveConversationId(starterConversation.id);
      return () => {
        cancelled = true;
      };
    }

    isHydratingHistoryRef.current = true;

    const loadConversationHistory = async () => {
      try {
        const chatsQuery = query(collection(db, 'chats'), where('userId', '==', user.uid));
        const snapshot = await getDocs(chatsQuery);

        if (cancelled) {
          return;
        }

        // Group chat docs (workspaceId set) hold no `messages` array - they'd
        // look empty here and get deleted, so they're left out entirely.
        const normalizedDocs = snapshot.docs
          .filter((snapshotDoc) => !snapshotDoc.data().workspaceId)
          .map((snapshotDoc) => normalizeConversation({ id: snapshotDoc.id, ...snapshotDoc.data() }, settings.language));

        const loadedConversations = normalizedDocs
          .filter((conversation) => conversation.messages.length > 0)
          .sort((left, right) => right.updatedAt - left.updatedAt);

        const emptyDocs = normalizedDocs.filter((conversation) => conversation.messages.length === 0);
        if (emptyDocs.length > 0) {
          Promise.all(emptyDocs.map((conversation) => deleteDoc(doc(db, 'chats', conversation.id)))).catch((error) => {
            console.error(error);
          });
        }

        const starterConversation = createConversation(Date.now(), settings.language);
        setConversations([starterConversation, ...loadedConversations]);
        setActiveConversationId(starterConversation.id);
      } catch (error) {
        console.error(error);
      } finally {
        if (!cancelled) {
          isHydratingHistoryRef.current = false;
        }
      }
    };

    loadConversationHistory();

    return () => {
      cancelled = true;
    };
    // settings.language intentionally excluded: this should only reload
    // history on login/logout, not re-fetch and mint a new conversation
    // every time the user flips the language toggle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid || isHydratingHistoryRef.current) {
      return;
    }

    const persistConversationHistory = async () => {
      try {
        await Promise.all(conversations
          .filter((conversation) => conversation.messages.length > 0)
          .map(async (conversation) => {
            const normalizedConversation = normalizeConversation({
              ...conversation,
              updatedAt: conversation.id === activeConversationId ? Date.now() : conversation.updatedAt
            }, settings.language);

            await setDoc(doc(db, 'chats', normalizedConversation.id), {
              userId: user.uid,
              title: normalizedConversation.title,
              messages: normalizedConversation.messages,
              updatedAt: normalizedConversation.updatedAt
            }, { merge: true });
          }));
      } catch (error) {
        console.error(error);
      }
    };

    persistConversationHistory();
    // settings.language intentionally excluded: flipping the language
    // toggle shouldn't trigger a Firestore write pass on its own.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConversationId, conversations, user?.uid]);

  const updateConversationById = (conversationId, updater) => {
    setConversations((prev) => prev.map((conversation) => {
      if (conversation.id !== conversationId) {
        return conversation;
      }

      return updater(conversation);
    }));
  };

  const updateActiveConversation = (updater) => updateConversationById(activeConversationId, updater);

  const [artifact, setArtifact] = useState(null);
  const [artifactTab, setArtifactTab] = useState('preview');

  const openArtifact = useCallback((data) => {
    setArtifact(data);
    setArtifactTab(data.doc ? 'preview' : 'code');
  }, []);

  const closeArtifact = () => setArtifact(null);

  // Stable reference across renders (only changes if the UI language
  // changes) so this doesn't get recreated on every message-list re-render.
  const markdownComponents = useMemo(() => ({
    code({ inline, className, children }) {
      const match = /language-(\w+)/.exec(className || '');
      return !inline && match ? (
        <CodeBlock
          language={match[1]}
          code={String(children).replace(/\n$/, '')}
          uiLang={settings.language}
          onOpenArtifact={openArtifact}
        />
      ) : (
        <code className="inline-code">{children}</code>
      );
    }
  }), [settings.language, openArtifact]);

  const setAiSpeakingBoth = (value) => {
    aiSpeakingRef.current = value;
    setAiSpeaking(value);
  };

  // One utterance of the voice conversation: transcribe it and send it
  // straight away - it never goes through the input box - then listen for
  // the next turn once the reply is done (or, while it's being spoken, for
  // the user talking over it).
  const handleVoiceUtterance = async (blob) => {
    setVoiceTranscribing(true);
    let texto = '';
    try {
      texto = await transcribeSpeech(blob, settings.language);
    } catch (error) {
      console.error(error);
      if (error.status === 503 || error.status === 403) {
        setVoiceNotice(t(error.status === 503 ? 'voiceUnavailable' : 'voicePlanRequired'));
        setVoiceTranscribing(false);
        stopVoiceMode();
        return;
      }
    }
    setVoiceTranscribing(false);
    if (!voiceModeActiveRef.current) return;
    if (texto) await sendMessageRef.current(texto, { autoSpeak: true });
    if (voiceModeActiveRef.current) voiceListenerRef.current?.resume();
  };

  const stopVoiceMode = () => {
    voiceModeActiveRef.current = false;
    setVoiceModeActive(false);
    voiceListenerRef.current?.close();
    voiceListenerRef.current = null;
    stopSpeaking();
    setAiSpeakingBoth(false);
  };

  // The mic button doubles as the voice-mode on/off switch: one click
  // starts a hands-free conversation (listen -> send -> speak reply ->
  // listen again) like ChatGPT/Gemini voice mode; clicking again at any
  // point - even mid-reply - stops it.
  const toggleMic = async () => {
    if (!micSupported) return;

    if (voiceModeActive) {
      stopVoiceMode();
      return;
    }

    setVoiceNotice('');
    voiceModeActiveRef.current = true;
    setVoiceModeActive(true);
    try {
      const listener = await openVoiceListener({
        onLevel: setMicLevel,
        onState: (state) => setMicActive(state === 'listening'),
        isAiSpeaking: () => aiSpeakingRef.current,
        // Barge-in: talking while the AI is speaking interrupts it, like a
        // real conversation.
        onSpeechStart: () => {
          if (aiSpeakingRef.current) {
            stopSpeaking();
            setAiSpeakingBoth(false);
          }
        },
        onUtterance: handleVoiceUtterance
      });
      if (!voiceModeActiveRef.current) {
        listener.close();
        return;
      }
      voiceListenerRef.current = listener;
      listener.resume();
    } catch (error) {
      console.error(error);
      setVoiceNotice(t('voiceMicDenied'));
      stopVoiceMode();
    }
  };

  // `kind` is forced by which menu item was used to open the picker
  // (Images/Videos/Files), rather than sniffed from file.type, so a
  // mislabeled or missing MIME type never lands an attachment in the
  // wrong bucket (images+videos share one cap, documents another - see
  // maxImages / MAX_DOCUMENT_ATTACHMENTS).
  const attachFiles = (files, kind) => {
    if (files.length === 0) return;

    setPendingAttachments((prev) => {
      const max = kind === 'document' ? MAX_DOCUMENT_ATTACHMENTS : maxImages;
      const countOfKind = prev.filter((a) => (kind === 'document' ? a.kind === 'document' : a.kind !== 'document')).length;
      const room = Math.max(max - countOfKind, 0);
      const accepted = files.slice(0, room).map((file) => ({
        id: `${Date.now()}-${Math.random()}`,
        file,
        kind,
        previewUrl: kind === 'document' ? null : URL.createObjectURL(file)
      }));
      return [...prev, ...accepted];
    });
  };

  const handleAttachFiles = (e, kind) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    attachFiles(files, kind);
  };

  const removePendingAttachment = (id) => {
    setPendingAttachments((prev) => {
      const target = prev.find((a) => a.id === id);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((a) => a.id !== id);
    });
  };

  useClickOutside(attachMenuRef, attachMenuOpen, setAttachMenuOpen);
  useClickOutside(voiceMenuRef, voiceMenuOpen, setVoiceMenuOpen);

  const toggleScreenShare = async () => {
    if (screenShareActive) {
      screenStreamRef.current?.getTracks().forEach((track) => track.stop());
      screenStreamRef.current = null;
      setScreenShareActive(false);
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
      screenStreamRef.current = stream;
      if (screenVideoRef.current) {
        screenVideoRef.current.srcObject = stream;
        await screenVideoRef.current.play().catch(() => {});
      }
      stream.getVideoTracks()[0].addEventListener('ended', () => {
        screenStreamRef.current = null;
        setScreenShareActive(false);
      });
      setScreenShareActive(true);
    } catch (error) {
      console.error(error);
    }
  };

  const sendMessage = async (rawMessage, { autoSpeak = false, historyOverride = null } = {}) => {
    const trimmedInput = rawMessage.trim();
    const hasAttachments = pendingAttachments.length > 0;
    if ((!trimmedInput && !hasAttachments && !screenShareActive) || loading) return;

    const conversationId = activeConversationId;
    const userMessage = trimmedInput || t('describeAttachmentFallback');
    // Recent turns only - the backend caps historial at 12 (see ChatRequest
    // in backend/app/schemas.py) and rejects longer conversations with a 422.
    // Each turn is cut to MAX_HISTORY_TEXT too: a past reply can be a whole
    // generated page, and the backend caps every turn's text at 4000.
    const historial = (historyOverride || activeConversation?.messages || [])
      .slice(-MAX_HISTORY_MESSAGES)
      .map(({ role, text }) => ({ role, text: String(text || '').slice(0, MAX_HISTORY_TEXT) }))
      .filter(({ text }) => text);
    const attachmentsToUpload = pendingAttachments;
    setInput("");
    setPendingAttachments([]);
    setLoading(true);
    setLoadingConversationId(conversationId);
    setAssistantStatus('thinking');
    setLoadingSources([]);
    setLoadingDesign(null);
    brainStore.current = { modelo: null, tokens: [] };
    trackEvent('send_message', {
      has_attachments: hasAttachments,
      voice_mode: autoSpeak,
      screen_share: screenShareActive
    });

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      const imagenes = [];
      const documentos = [];
      const displayAttachments = [];

      for (const item of attachmentsToUpload) {
        if (item.kind === 'video') {
          const [fileUrl, frameBlob] = await Promise.all([
            uploadAttachment(item.file, user.uid, conversationId),
            captureVideoFrameFromFile(item.file)
          ]);
          displayAttachments.push({ type: 'video', url: fileUrl, name: item.file.name });
          imagenes.push(await uploadBlob(frameBlob, user.uid, conversationId, `${item.file.name}-frame.jpg`));
        } else if (item.kind === 'document') {
          const url = await uploadAttachment(item.file, user.uid, conversationId);
          documentos.push(url);
          displayAttachments.push({ type: 'document', url, name: item.file.name });
        } else {
          const url = await uploadAttachment(item.file, user.uid, conversationId);
          imagenes.push(url);
          displayAttachments.push({ type: 'image', url, name: item.file.name });
        }
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
      }

      if (screenShareActive && screenVideoRef.current) {
        try {
          const frameBlob = await captureStreamFrame(screenVideoRef.current);
          imagenes.push(await uploadBlob(frameBlob, user.uid, conversationId, 'screen-frame.jpg'));
        } catch (captureError) {
          console.error(captureError);
        }
      }

      updateConversationById(conversationId, (conversation) => ({
        ...conversation,
        title: conversation.messages.length <= 1 ? buildConversationTitle(userMessage, settings.language) : conversation.title,
        messages: [...conversation.messages, {
          role: 'user',
          text: userMessage,
          ...(displayAttachments.length > 0 ? { attachments: displayAttachments } : {})
        }],
        updatedAt: Date.now()
      }));

      const data = await wheniseeusAI({
        mensaje: userMessage,
        historial,
        imagenes: imagenes.slice(0, maxImages),
        documentos: documentos.slice(0, MAX_DOCUMENT_ATTACHMENTS),
        modoVoz: autoSpeak || settings.readAloud,
        // Activations are a few KB per token: only asked for while the panel is open.
        cerebro: brainOpen,
        signal: abortController.signal,
        onStatus: (event) => {
          if (event.status === 'cerebro') {
            if (event.reset) brainStore.current = { modelo: event.modelo, tokens: [] };
            else brainStore.current.tokens.push(...(event.tokens || []));
            return;
          }
          setAssistantStatus(event.status);
          if (event.status === 'sources' && Array.isArray(event.fuentes)) {
            setLoadingSources(event.fuentes);
          }
          if (event.status === 'designing' && event.diseno) {
            setLoadingDesign(event.diseno);
          }
        }
      });

      updateConversationById(conversationId, (conversation) => ({
        ...conversation,
        messages: [...conversation.messages, {
          role: 'ai',
          text: data.texto,
          fuentes: data.fuentes,
          ...(data.buscoEnGoogle ? { googleResultados: true } : {})
        }],
        updatedAt: Date.now()
      }));

      // Auto-open the artifact panel for freshly generated previewable
      // code, matching Claude's Artifacts behavior.
      const newArtifact = detectPageArtifact(data.texto, settings.language);
      if (newArtifact) {
        openArtifact(newArtifact);
      }

      if (settings.readAloud || autoSpeak) {
        setAiSpeakingBoth(true);
        speak(data.texto, settings.language, settings.voiceURI, {
          onEnd: () => { setAiSpeakingBoth(false); setSpeakLevel(0); },
          onLevel: setSpeakLevel
        });
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        // The user tapped "stop" - not an error, just a request to give up
        // on this reply, so no error bubble in the conversation.
        return;
      }
      console.error(error);
      if (error.status === 401) {
        // The Firebase ID token was rejected server-side (expired/revoked) -
        // a generic "something went wrong" bubble would just leave the user
        // stuck resending into the same 401, so sign them out and let the
        // auth modal prompt them to log back in instead.
        updateConversationById(conversationId, (conversation) => ({
          ...conversation,
          messages: [...conversation.messages, { role: 'ai', text: t('errSessionExpired') }],
          updatedAt: Date.now()
        }));
        await signOutUser().catch((signOutError) => console.error(signOutError));
        setShowAuthModal(true);
      } else {
        updateConversationById(conversationId, (conversation) => ({
          ...conversation,
          messages: [...conversation.messages, { role: 'ai', text: t('errorProcessing') }],
          updatedAt: Date.now()
        }));
      }
    } finally {
      abortControllerRef.current = null;
      setLoading(false);
      setLoadingConversationId(null);
    }
  };

  sendMessageRef.current = sendMessage;

  // Releases the mic if the app goes away mid-conversation.
  useEffect(() => () => voiceListenerRef.current?.close(), []);

  const stopGenerating = () => {
    abortControllerRef.current?.abort();
  };

  const handleSend = (e) => {
    e.preventDefault();
    sendMessage(input, { autoSpeak: voiceModeActive });
  };

  const handleRetryMessage = (index) => {
    if (loading) return;
    const priorUserMessage = chat[index - 1];
    if (!priorUserMessage || priorUserMessage.role !== 'user') return;

    const truncatedHistory = chat.slice(0, index - 1);
    setMessageFeedback((prev) => {
      const next = { ...prev };
      delete next[`${activeConversationId}:${index}`];
      return next;
    });
    updateActiveConversation((conversation) => ({
      ...conversation,
      messages: truncatedHistory
    }));
    sendMessage(priorUserMessage.text, { historyOverride: truncatedHistory });
  };

  const handleMessageFeedback = (index, value) => {
    const key = `${activeConversationId}:${index}`;
    setMessageFeedback((prev) => ({
      ...prev,
      [key]: prev[key] === value ? null : value
    }));
  };

  const handleNewChat = () => {
    const nextId = String(Date.now());
    setConversations((prev) => [createConversation(nextId, settings.language), ...prev]);
    setActiveConversationId(nextId);
    setHistoryPanelOpen(false);
    setInput("");
  };

  const handleSelectConversation = (conversationId) => {
    setActiveConversationId(conversationId);
    setHistoryPanelOpen(false);
    setInput("");
  };

  const handleDeleteConversation = async (e, conversationId) => {
    e.stopPropagation();
    if (user?.uid) {
      try {
        await deleteDoc(doc(db, 'chats', conversationId));
      } catch (error) {
        console.error(error);
      }
    }
    setConversations((prev) => {
      const next = prev.filter((c) => c.id !== conversationId);
      if (next.length === 0) {
        const newConv = createConversation(String(Date.now()), settings.language);
        setActiveConversationId(newConv.id);
        return [newConv];
      }
      if (conversationId === activeConversationId) {
        const nextOrdered = [...next].sort((left, right) => right.updatedAt - left.updatedAt);
        setActiveConversationId(nextOrdered[0].id);
      }
      return next;
    });
  };

  const handleAuthFieldChange = (e) => {
    const { name, value } = e.target;
    setAuthForm((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const resetAuthState = () => {
    setAuthError('');
    setAuthInfo('');
    setAuthForm({ email: '', password: '', confirmPassword: '' });
    setShowPassword(false);
    setShowConfirmPassword(false);
    setAcceptTerms(false);
  };

  const handleOpenAuthModal = () => {
    resetAuthState();
    setAuthMode('login');
    setShowAuthModal(true);
  };

  const handleCloseAuthModal = () => {
    resetAuthState();
    setShowAuthModal(false);
  };

  const handleResetPassword = async () => {
    const email = authForm.email.trim();

    if (!email) {
      setAuthInfo('');
      setAuthError(t('enterEmailToReset'));
      return;
    }

    setAuthLoading(true);
    setAuthError('');
    setAuthInfo('');

    try {
      await sendPasswordReset(email, settings.language);
      setAuthInfo(t('resetPasswordSent'));
    } catch (error) {
      console.error(error);
      setAuthError(getAuthErrorMessage(error?.code, 'login'));
    } finally {
      setAuthLoading(false);
    }
  };

  const getAuthErrorMessage = (errorCode, mode) => {
    switch (errorCode) {
      case 'auth/invalid-credential':
      case 'auth/wrong-password':
      case 'auth/user-not-found':
        return mode === 'login' ? t('errIncorrectCredentials') : t('errCantCreateAccount');
      case 'auth/email-already-in-use':
        return t('errEmailInUse');
      case 'auth/invalid-email':
        return t('errInvalidEmail');
      case 'auth/weak-password':
        return t('errWeakPassword');
      case 'auth/too-many-requests':
        return t('errTooManyRequests');
      case 'auth/network-request-failed':
        return t('errNetwork');
      case 'auth/operation-not-allowed':
        return t('errEmailPasswordDisabled');
      case 'auth/account-exists-with-different-credential':
        return t('errGoogleAccountExists');
      case 'auth/unauthorized-domain':
        return t('errGoogleUnauthorizedDomain');
      case 'auth/popup-blocked':
        return t('errGooglePopupBlocked');
      default:
        return t('errGenericAuth');
    }
  };

  const handleSubmitAuth = async (e) => {
    e.preventDefault();

    if (!authForm.email.trim() || !authForm.password.trim()) {
      setAuthError(t('errEnterEmailPassword'));
      return;
    }

    if (authMode === 'register') {
      if (authForm.password !== authForm.confirmPassword) {
        setAuthError(t('errPasswordsMismatch'));
        return;
      }
      if (!acceptTerms) {
        setAuthError(t('errMustAcceptTerms'));
        return;
      }
    }

    setAuthLoading(true);
    setAuthError('');
    setAuthInfo('');

    try {
      if (authMode === 'login') {
        await signInWithEmail(authForm.email.trim(), authForm.password);
        trackEvent('login', { method: 'password' });
      } else {
        await registerWithEmail(authForm.email.trim(), authForm.password);
        trackEvent('sign_up', { method: 'password' });
        // Fire-and-forget - an internal heads-up email, not something that
        // should ever block or fail the signup itself if it hiccups.
        notificarRegistro().catch(() => {});
      }

      handleCloseAuthModal();
    } catch (error) {
      console.error(error);
      setAuthError(getAuthErrorMessage(error?.code, authMode));
    } finally {
      setAuthLoading(false);
    }
  };

  // Google sign-in (popup on computers, redirect on phones - see
  // src/firebase.js's signInWithGoogle). Signing in with Google creates the
  // account on first use, so the terms are accepted by continuing, as the
  // note under the button says.
  const finishGoogleSignIn = (result) => {
    if (!result) return;
    trackEvent(result.isNewUser ? 'sign_up' : 'login', { method: 'google' });
    if (result.isNewUser) notificarRegistro().catch(() => {});
    handleCloseAuthModal();
  };

  const handleGoogleAuth = async () => {
    setAuthLoading(true);
    setAuthError('');
    setAuthInfo('');
    try {
      finishGoogleSignIn(await signInWithGoogle());
    } catch (error) {
      if (error?.code !== 'auth/popup-closed-by-user' && error?.code !== 'auth/cancelled-popup-request') {
        console.error(error);
        setAuthError(getAuthErrorMessage(error?.code, authMode));
      }
    } finally {
      setAuthLoading(false);
    }
  };

  // Coming back from Google's page after a redirect sign-in (mobile).
  useEffect(() => {
    completeGoogleRedirect()
      .then(finishGoogleSignIn)
      .catch((error) => {
        console.error(error);
        setAuthError(getAuthErrorMessage(error?.code, 'login'));
        setShowAuthModal(true);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSignOut = async () => {
    try {
      await signOutUser();
    } catch (error) {
      console.error(error);
      setAuthError(t('errSignOut'));
      setShowAuthModal(true);
    }
  };

  const handleSupportFieldChange = (e) => {
    const { name, value } = e.target;
    setSupportForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmitSupport = async (e) => {
    e.preventDefault();

    if (!supportForm.nombre.trim() || !supportForm.email.trim() || supportForm.mensaje.trim().length < 5) {
      setSupportError(t('errSupportRequired'));
      return;
    }

    setSupportSending(true);
    setSupportError('');

    try {
      await enviarConsultaSoporte({
        nombre: supportForm.nombre.trim(),
        email: supportForm.email.trim(),
        mensaje: supportForm.mensaje.trim()
      });
      setSupportSent(true);
      setSupportForm((prev) => ({ ...prev, mensaje: '' }));
    } catch (error) {
      console.error(error);
      setSupportError(t('errSupportSend'));
    } finally {
      setSupportSending(false);
    }
  };

  if (!authResolved || goingToLanding) {
    return (
      <div className="auth-screen-loading">
        {pullToRefreshIndicator}
        <img className="auth-screen-loading-logo" src={LOGO_SRC} alt="wheniseeus" />
      </div>
    );
  }

  if (!user) {
    const switchAuthMode = (mode) => {
      setAuthMode(mode);
      setAuthError('');
      setAuthInfo('');
    };

    return (
      <div className="auth-screen">
        {pullToRefreshIndicator}
        <LanguageToggle
          className="lang-toggle-dark"
          language={settings.language}
          onChange={(lang) => updateSetting('language', lang)}
        />
        <div className="auth-showcase">
          <h2 className="auth-showcase-heading">
            {t('showcaseHeadingLine1')}<br /><span>{t('showcaseHeadingLine2')}</span>
          </h2>
          <div className="showcase-team-row">
            <div className="showcase-avatar-stack">
              <span className="showcase-avatar showcase-avatar-1">J</span>
              <span className="showcase-avatar showcase-avatar-2">A</span>
              <span className="showcase-avatar showcase-avatar-3">S</span>
            </div>
            <span className="showcase-team-caption">{t('showcaseTeamCaption')}</span>
          </div>
          <div className="auth-showcase-graphic">
            <div className="showcase-card showcase-card-back">
              <div className="showcase-bubble ai">{t('showcaseAiBubble1')}</div>
            </div>
            <div className="showcase-card showcase-card-front">
              <div className="showcase-bubble user">{t('showcaseUserBubble')}</div>
              <div className="showcase-bubble ai">{t('showcaseAiBubble2')}</div>
            </div>
            <span className="showcase-emoji showcase-emoji-1" aria-hidden="true">✨</span>
            <span className="showcase-emoji showcase-emoji-2" aria-hidden="true">💬</span>
          </div>
        </div>

        <div className="auth-panel">
          <div className="auth-panel-inner">
            <div className="auth-brand">
              <div className="auth-brand-badge">
                <img src={LOGO_SRC} alt="wheniseeus logo" />
              </div>
              <h1>wheniseeus</h1>
            </div>

            <form className="auth-form" onSubmit={handleSubmitAuth}>
              <input
                className="auth-input"
                type="email"
                name="email"
                placeholder={t('emailPlaceholder')}
                value={authForm.email}
                onChange={handleAuthFieldChange}
                disabled={authLoading}
              />
              <div className="auth-input-wrapper">
                <input
                  className="auth-input"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  placeholder={t('passwordPlaceholder')}
                  value={authForm.password}
                  onChange={handleAuthFieldChange}
                  disabled={authLoading}
                />
                <button
                  type="button"
                  className="auth-password-toggle"
                  onClick={() => setShowPassword((prev) => !prev)}
                  tabIndex={-1}
                  aria-label={showPassword ? t('hidePassword') : t('showPassword')}
                >
                  {showPassword ? Icons.eyeOff : Icons.eye}
                </button>
              </div>

              {authMode === 'register' && (
                <div className="auth-input-wrapper">
                  <input
                    className="auth-input"
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    placeholder={t('confirmPasswordPlaceholder')}
                    value={authForm.confirmPassword}
                    onChange={handleAuthFieldChange}
                    disabled={authLoading}
                  />
                  <button
                    type="button"
                    className="auth-password-toggle"
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    tabIndex={-1}
                    aria-label={showConfirmPassword ? t('hidePassword') : t('showPassword')}
                  >
                    {showConfirmPassword ? Icons.eyeOff : Icons.eye}
                  </button>
                </div>
              )}

              {authMode === 'register' && (
                <label className="auth-terms-row">
                  <input
                    type="checkbox"
                    checked={acceptTerms}
                    onChange={(e) => setAcceptTerms(e.target.checked)}
                    disabled={authLoading}
                  />
                  <span>
                    {t('termsAcceptPrefix')} <a href="/terminos.html" target="_blank" rel="noreferrer">{t('termsAndConditions')}</a>,{' '}
                    {t('termsMidPrivacy')} <a href="/privacidad.html" target="_blank" rel="noreferrer">{t('privacyPolicy')}</a>{' '}
                    {t('termsMidRefund')} <a href="/reembolsos.html" target="_blank" rel="noreferrer">{t('refundPolicy')}</a>.
                  </span>
                </label>
              )}

              {authInfo && <p className="auth-success">{authInfo}</p>}
              {authError && <p className="auth-error">{authError}</p>}
              <button className="auth-submit-btn" type="submit" disabled={authLoading}>
                {authLoading ? t('processing') : authMode === 'login' ? t('logIn') : t('createAccount')}
              </button>
            </form>

            <div className="auth-or" role="separator"><span>{t('orDivider')}</span></div>
            <button className="auth-google-btn" type="button" onClick={handleGoogleAuth} disabled={authLoading}>
              <img className="auth-google-logo" src="/google-g-logo.svg" alt="" width="18" height="18" />
              <span>{t('continueWithGoogle')}</span>
            </button>
            <p className="auth-google-note">
              {t('googleTermsPrefix')} <a href="/terminos.html" target="_blank" rel="noreferrer">{t('termsAndConditions')}</a>{' '}
              {t('googleTermsAnd')} <a href="/privacidad.html" target="_blank" rel="noreferrer">{t('privacyPolicy')}</a>.
            </p>

            {authMode === 'login' && (
              <button
                className="auth-link-btn auth-forgot-link"
                type="button"
                onClick={handleResetPassword}
                disabled={authLoading}
              >
                {t('forgotPassword')}
              </button>
            )}

            <div className="auth-switch-box">
              {authMode === 'login' ? (
                <p>
                  {t('noAccount')}{' '}
                  <button type="button" onClick={() => switchAuthMode('register')}>{t('signUp')}</button>
                </p>
              ) : (
                <p>
                  {t('alreadyHaveAccount')}{' '}
                  <button type="button" onClick={() => switchAuthMode('login')}>{t('logIn')}</button>
                </p>
              )}
            </div>
          </div>

          <div className="auth-footer-links">
            <a href="/terminos.html" target="_blank" rel="noreferrer">{t('termsAndConditions')}</a>
            <a href="/privacidad.html" target="_blank" rel="noreferrer">{t('privacyPolicy')}</a>
            <a href="/reembolsos.html" target="_blank" rel="noreferrer">{t('refundPolicy')}</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      <aside className="app-sidebar">
        <div className="sidebar-brand">
          <img className="sidebar-logo" src={LOGO_SRC} alt="wheniseeus logo" />
          <div className="brand-title">
            <h1>Wheniseeus</h1>
          </div>
        </div>

        <button type="button" className="sidebar-new-chat-btn" onClick={handleNewChat}>
          {Icons.add}
          <span>{t('newConversationTitle')}</span>
        </button>

        <div className="sidebar-history">
          <span className="sidebar-section-label">{t('menuHistory')}</span>
          <div className="sidebar-history-list" onScroll={handleHistoryScroll}>
            {visibleConversations.map((conversation) => {
              const preview = conversation.messages[conversation.messages.length - 1]?.text || t('noMessages');

              return (
                <div
                  key={conversation.id}
                  className={`history-item ${conversation.id === activeConversationId ? 'active' : ''}`}
                  onClick={() => handleSelectConversation(conversation.id)}
                >
                  <div className="history-item-text">
                    <strong>{conversation.title}</strong>
                    <span>{preview}</span>
                  </div>
                  <button
                    className="history-item-delete"
                    type="button"
                    title={t('deleteConversationTitle')}
                    onClick={(e) => handleDeleteConversation(e, conversation.id)}
                  >
                    {Icons.delete}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        <nav className="sidebar-bottom-nav">
          <LanguageToggle
            className="sidebar-lang-toggle"
            language={settings.language}
            onChange={(lang) => updateSetting('language', lang)}
          />
          <button type="button" className="sidebar-nav-item" onClick={() => navigate('/workspaces')}>
            {Icons.team}
            <span>{t('workspacesTitle')}</span>
          </button>
          <button type="button" className="sidebar-nav-item" onClick={() => navigate('/vscode')}>
            {Icons.code}
            <span>{t('remoteTitle')}</span>
          </button>
          <a className="sidebar-nav-item" href={VSCODE_EXTENSION_URL} target="_blank" rel="noreferrer">
            {Icons.code}
            <span>{t('vscodeExtensionLink')}</span>
          </a>
          <button type="button" className="sidebar-nav-item" onClick={() => setShowSettings(true)}>
            {Icons.settings}
            <span>{t('menuSettings')}</span>
          </button>
          <button
            type="button"
            className="sidebar-nav-item"
            onClick={() => {
              setSupportForm((prev) => ({ ...prev, email: user?.email || prev.email }));
              setSupportSent(false);
              setSupportError('');
              setShowSupport(true);
            }}
          >
            {Icons.support}
            <span>{t('menuSupport')}</span>
          </button>
          <button type="button" className="sidebar-nav-item" onClick={handleOpenAuthModal}>
            {Icons.user}
            <span>{t('menuAccount')}</span>
          </button>
        </nav>
      </aside>

      <div className="app-main-column">
      <header className="app-header">
        <div className="header-left">
          <img className="app-logo" src={LOGO_SRC} alt="wheniseeus logo" />
          <div className="brand-title">
            <h1>Wheniseeus</h1>
          </div>
        </div>

        <div className="header-right">
          <button
            className="header-btn"
            type="button"
            title={t('newConversationTitle')}
            aria-label={t('newConversationTitle')}
            onClick={handleNewChat}
          >
            {Icons.add}
          </button>
          <button
            className="header-btn menu-toggle-btn"
            type="button"
            title={t('menuTitle')}
            aria-label={t('openMenuAria')}
            onClick={() => setMenuOpen(true)}
          >
            {Icons.menu}
          </button>
        </div>
      </header>

      <main className="chat-panel">
        <div className="chat-content">
          {pullToRefreshIndicator}
          {chat.length === 0 ? (
            <div className="chat-empty-state">
              <img className="chat-empty-logo" src={LOGO_SRC} alt="wheniseeus logo" />
              <h2>Wheniseeus</h2>
              <p>{t('chatEmptyGreeting')}</p>
            </div>
          ) : (
            <section className="chat-window">
              {chat.map((msg, i) => (
                <div key={i} className={`message-wrapper ${msg.role}`}>
                  <div className="message-bubble">
                    {msg.attachments && msg.attachments.length > 0 && (
                      <div className="message-attachments">
                        {msg.attachments.map((a, idx) => (
                          a.type === 'video' ? (
                            <video key={idx} src={a.url} controls className="message-attachment-media" />
                          ) : a.type === 'document' ? (
                            <a
                              key={idx}
                              href={a.url}
                              target="_blank"
                              rel="noreferrer"
                              className="message-attachment-document"
                            >
                              {Icons.document}
                              <span>{a.name}</span>
                            </a>
                          ) : (
                            <img key={idx} src={a.url} alt={a.name || 'attachment'} className="message-attachment-media" />
                          )
                        ))}
                      </div>
                    )}
                    <ReactMarkdown components={markdownComponents}>
                      {msg.text}
                    </ReactMarkdown>
                    {msg.role === 'ai' && (
                      <AiMessageArtifactCard text={msg.text} uiLang={settings.language} onOpenArtifact={openArtifact} />
                    )}
                    {msg.fuentes && msg.fuentes.length > 0 && (
                      <div className="sources">
                        <small>{t('sourcesLabel')}</small>
                        {msg.fuentes.filter((f) => isWebUrl(f?.link)).map((f, idx) => {
                          const hostname = hostnameOf(f.link);
                          return (
                            <a
                              key={idx}
                              href={f.link}
                              target="_blank"
                              rel="noreferrer"
                              className="source-pill"
                              title={f.title || hostname}
                            >
                              {hostname && (
                                <img
                                  src={`https://www.google.com/s2/favicons?domain=${hostname}&sz=32`}
                                  alt=""
                                  className="source-favicon"
                                />
                              )}
                              <span className="source-title">{hostname || f.title}</span>
                            </a>
                          );
                        })}
                      </div>
                    )}
                    {msg.googleResultados && i === lastGoogleSearchIndex && <GoogleResults />}
                    {msg.role === 'ai' && msg.text && (
                      <MessageActionsRow
                        text={msg.text}
                        uiLang={settings.language}
                        showSpeak={ttsSupported && tier !== 'basico'}
                        onSpeak={() => speak(msg.text, settings.language, settings.voiceURI)}
                        canRetry={!loading && chat[i - 1]?.role === 'user'}
                        onRetry={() => handleRetryMessage(i)}
                        feedback={messageFeedback[`${activeConversationId}:${i}`]}
                        onFeedback={(value) => handleMessageFeedback(i, value)}
                      />
                    )}
                  </div>
                </div>
              ))}
              {loading && activeConversationId === loadingConversationId && (
                <div className="loading">
                  <div className="loading-bubble">
                    {assistantStatus === 'sources' && loadingSources.length > 0 ? (
                      <div className="loading-favicons">
                        {loadingSources.slice(0, 5).map((f, idx) => {
                          const hostname = hostnameOf(f.link);
                          return hostname ? (
                            <img
                              key={idx}
                              src={`https://www.google.com/s2/favicons?domain=${hostname}&sz=32`}
                              alt=""
                              className="loading-favicon"
                              style={{ animationDelay: `${idx * 0.12}s, ${idx * 0.2}s` }}
                            />
                          ) : null;
                        })}
                      </div>
                    ) : assistantStatus === 'searching' ? (
                      <span className="loading-orb" />
                    ) : (
                      <EyeBadge label={t('voiceModeThinking')} />
                    )}
                    <LoadingStatus
                      status={assistantStatus}
                      diseno={loadingDesign}
                      sourcesCount={loadingSources.length}
                      language={settings.language}
                    />
                    {showLongWaitNotice && (
                      <span className="loading-notice">{t('longWaitNotice')}</span>
                    )}
                    <button type="button" className="brain-toggle" aria-pressed={brainOpen} onClick={toggleBrain}>
                      {t(brainOpen ? 'brainHide' : 'brainShow')}
                    </button>
                  </div>
                  {brainOpen && (
                    <BrainPanel
                      store={brainStore}
                      language={settings.language}
                    />
                  )}
                </div>
              )}
              <div ref={chatEndRef} />
            </section>
          )}

          {pendingAttachments.length > 0 && (
            <div className="input-attachments-preview">
              {pendingAttachments.map((a) => (
                <div key={a.id} className={`input-attachment-thumb${a.kind === 'document' ? ' document' : ''}`}>
                  {a.kind === 'video' ? (
                    <video src={a.previewUrl} className="input-attachment-thumb-media" />
                  ) : a.kind === 'document' ? (
                    <>
                      {Icons.document}
                      <span className="input-attachment-thumb-name">{a.file.name}</span>
                    </>
                  ) : (
                    <img src={a.previewUrl} alt={a.file.name} className="input-attachment-thumb-media" />
                  )}
                  <button
                    type="button"
                    className="input-attachment-remove-btn"
                    onClick={() => removePendingAttachment(a.id)}
                    aria-label={t('removeAttachmentAria')}
                  >
                    {Icons.close}
                  </button>
                </div>
              ))}
            </div>
          )}

          {voiceNotice && !voiceModeActive && (
            <div className="voice-mode-status">
              <span>{voiceNotice}</span>
            </div>
          )}

          {voiceModeActive && (
            <div className="voice-mode-status">
              <div
                className={`voice-mode-orb${aiSpeaking ? ' speaking' : micActive ? ' listening' : ' thinking'}`}
                // The sphere itself never moves - only the gradient inside
                // it does (see .voice-mode-orb::before/::after in App.css),
                // its amplitude driven live by this CSS var from whichever
                // real audio source is active: the mic while listening, the
                // TTS playback while speaking.
                style={{ '--voice-level': aiSpeaking ? speakLevel : micActive ? micLevel : 0 }}
              />
              <span>
                {aiSpeaking
                  ? t('voiceModeSpeaking')
                  : loading
                  ? t(assistantStatus === 'searching' ? 'voiceModeSearching' : 'voiceModeThinking')
                  : voiceTranscribing
                  ? t('voiceModeThinking')
                  : t('voiceModeListening')}
              </span>
            </div>
          )}

          <div className={`screen-share-preview${screenShareActive ? '' : ' hidden'}`}>
            <video ref={screenVideoRef} muted playsInline className="screen-share-preview-video" />
            <span className="screen-share-badge">{t('screenSharing')}</span>
            <button
              type="button"
              className="screen-share-stop-btn"
              onClick={toggleScreenShare}
              aria-label={t('stopScreenShareAria')}
            >
              {Icons.close}
            </button>
          </div>

          <form className="input-area" onSubmit={handleSend}>
            <div className="input-shell">
              <input type="file" accept="image/*" multiple hidden ref={imageInputRef} onChange={(e) => handleAttachFiles(e, 'image')} />
              <input type="file" accept="video/*" multiple hidden ref={videoInputRef} onChange={(e) => handleAttachFiles(e, 'video')} />
              <input type="file" accept={DOCUMENT_ACCEPT} multiple hidden ref={documentInputRef} onChange={(e) => handleAttachFiles(e, 'document')} />
              {tier !== 'basico' && (
                <div className="attach-menu-wrapper" ref={attachMenuRef}>
                  <button
                    type="button"
                    className="composer-icon-btn"
                    onClick={() => setAttachMenuOpen((open) => !open)}
                    disabled={loading}
                    aria-label={t('attachFileAria')}
                    aria-expanded={attachMenuOpen}
                  >
                    {Icons.attach}
                  </button>
                  {attachMenuOpen && (
                    <div className="attach-menu">
                      <button
                        type="button"
                        className="attach-menu-item"
                        onClick={() => { setAttachMenuOpen(false); imageInputRef.current?.click(); }}
                        disabled={pendingAttachments.filter((a) => a.kind !== 'document').length >= maxImages}
                      >
                        {Icons.image}
                        {t('attachImages')}
                      </button>
                      <button
                        type="button"
                        className="attach-menu-item"
                        onClick={() => { setAttachMenuOpen(false); videoInputRef.current?.click(); }}
                        disabled={pendingAttachments.filter((a) => a.kind !== 'document').length >= maxImages}
                      >
                        {Icons.video}
                        {t('attachVideos')}
                      </button>
                      <button
                        type="button"
                        className="attach-menu-item"
                        onClick={() => { setAttachMenuOpen(false); documentInputRef.current?.click(); }}
                        disabled={pendingAttachments.filter((a) => a.kind === 'document').length >= MAX_DOCUMENT_ATTACHMENTS}
                      >
                        {Icons.document}
                        {t('attachFiles')}
                      </button>
                    </div>
                  )}
                </div>
              )}
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t('typeQuestionPlaceholder')}
                disabled={loading}
              />
              {micSupported && tier !== 'basico' && (
                <button
                  type="button"
                  className={`composer-icon-btn${micActive || aiSpeaking ? ' active' : ''}`}
                  onClick={toggleMic}
                  aria-label={voiceModeActive ? t('stopVoiceModeAria') : t('micAria')}
                >
                  {Icons.mic}
                </button>
              )}
              {screenShareSupported && tier !== 'basico' && (
                <button
                  type="button"
                  className={`composer-icon-btn${screenShareActive ? ' active' : ''}`}
                  onClick={toggleScreenShare}
                  aria-label={t('screenShareAria')}
                >
                  {Icons.screenShare}
                </button>
              )}
              {loading ? (
                <button
                  type="button"
                  className="composer-send-btn"
                  onClick={stopGenerating}
                  aria-label={t('stopGeneratingAria')}
                >
                  {Icons.stop}
                </button>
              ) : (
                <button type="submit" className="composer-send-btn" aria-label={t('sendMessageAria')}>
                  {Icons.send}
                </button>
              )}
            </div>
          </form>
        </div>
      </main>
      </div>

      <ArtifactPanel
        artifact={artifact}
        tab={artifactTab}
        onTabChange={setArtifactTab}
        onClose={closeArtifact}
        uiLang={settings.language}
      />

      {menuOpen && (
        <div className="drawer-overlay menu-overlay" onClick={() => setMenuOpen(false)}>
          <aside className="side-drawer menu-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <span>{t('menuTitle')}</span>
              <button
                className="drawer-close-btn"
                type="button"
                onClick={() => setMenuOpen(false)}
                aria-label={t('closeMenuAria')}
              >
                {Icons.close}
              </button>
            </div>

            <nav className="menu-list">
              <LanguageToggle
                className="menu-lang-toggle"
                language={settings.language}
                onChange={(lang) => updateSetting('language', lang)}
              />
              <button
                type="button"
                className="menu-item"
                onClick={() => { setMenuOpen(false); setHistoryPanelOpen(true); }}
              >
                {Icons.clock}
                <span>{t('menuHistory')}</span>
              </button>
              <button
                type="button"
                className="menu-item"
                onClick={() => { setMenuOpen(false); navigate('/workspaces'); }}
              >
                {Icons.team}
                <span>{t('workspacesTitle')}</span>
              </button>
              <button
                type="button"
                className="menu-item"
                onClick={() => { setMenuOpen(false); navigate('/vscode'); }}
              >
                {Icons.code}
                <span>{t('remoteTitle')}</span>
              </button>
              <a
                className="menu-item"
                href={VSCODE_EXTENSION_URL}
                target="_blank"
                rel="noreferrer"
                onClick={() => setMenuOpen(false)}
              >
                {Icons.code}
                <span>{t('vscodeExtensionLink')}</span>
              </a>
              <button
                type="button"
                className="menu-item"
                onClick={() => { setMenuOpen(false); setShowSettings(true); }}
              >
                {Icons.settings}
                <span>{t('menuSettings')}</span>
              </button>
              <button
                type="button"
                className="menu-item"
                onClick={() => {
                  setMenuOpen(false);
                  setSupportForm((prev) => ({ ...prev, email: user?.email || prev.email }));
                  setSupportSent(false);
                  setSupportError('');
                  setShowSupport(true);
                }}
              >
                {Icons.support}
                <span>{t('menuSupport')}</span>
              </button>
              <button
                type="button"
                className="menu-item"
                onClick={() => { setMenuOpen(false); handleOpenAuthModal(); }}
              >
                {Icons.user}
                <span>{t('menuAccount')}</span>
              </button>
            </nav>
          </aside>
        </div>
      )}

      {historyPanelOpen && (
        <div className="drawer-overlay history-overlay" onClick={() => setHistoryPanelOpen(false)}>
          <aside className="side-drawer history-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <span>{t('menuHistory')}</span>
              <div className="drawer-header-actions">
                <button
                  className="history-add-btn"
                  type="button"
                  title={t('newConversationTitle')}
                  onClick={handleNewChat}
                >
                  {Icons.add}
                </button>
                <button
                  className="drawer-close-btn"
                  type="button"
                  onClick={() => setHistoryPanelOpen(false)}
                  aria-label={t('closeHistoryAria')}
                >
                  {Icons.close}
                </button>
              </div>
            </div>

            <div className="history-list" onScroll={handleHistoryScroll}>
              {visibleConversations.map((conversation) => {
                const preview = conversation.messages[conversation.messages.length - 1]?.text || t('noMessages');

                return (
                  <div
                    key={conversation.id}
                    className={`history-item ${conversation.id === activeConversationId ? 'active' : ''}`}
                    onClick={() => handleSelectConversation(conversation.id)}
                  >
                    <div className="history-item-text">
                      <strong>{conversation.title}</strong>
                      <span>{preview}</span>
                    </div>
                    <button
                      className="history-item-delete"
                      type="button"
                      title={t('deleteConversationTitle')}
                      onClick={(e) => handleDeleteConversation(e, conversation.id)}
                    >
                      {Icons.delete}
                    </button>
                  </div>
                );
              })}
            </div>
          </aside>
        </div>
      )}

      {showSettings && (
        <div className="settings-screen">
          <header className="settings-screen-header">
            <div className="settings-screen-titlebar">
              <button
                className="settings-back-icon-btn"
                type="button"
                onClick={() => setShowSettings(false)}
                aria-label={t('backAria')}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 20, height: 20 }}>
                  <path d="M19 12H5M12 19l-7-7 7-7" />
                </svg>
              </button>
              <h2>{t('menuSettings')}</h2>
            </div>
          </header>

          <div className="settings-screen-content">
            <div className="settings-section">
              <h3>{t('appearance')}</h3>
              <div className="settings-row">
                <span className="settings-label">{t('theme')}</span>
                <div className="settings-toggle-group">
                  <button
                    className={`settings-toggle-btn${settings.theme === 'light' ? ' active' : ''}`}
                    onClick={() => updateSetting('theme', 'light')}
                    type="button"
                  >
                    {Icons.lightMode} {t('light')}
                  </button>
                  <button
                    className={`settings-toggle-btn${settings.theme === 'dark' ? ' active' : ''}`}
                    onClick={() => updateSetting('theme', 'dark')}
                    type="button"
                  >
                    {Icons.darkMode} {t('dark')}
                  </button>
                  <button
                    className={`settings-toggle-btn${settings.theme === 'system' ? ' active' : ''}`}
                    onClick={() => updateSetting('theme', 'system')}
                    type="button"
                  >
                    {Icons.autoMode} {t('themeAuto')}
                  </button>
                </div>
              </div>
              <div className="settings-row">
                <span className="settings-label">{t('textSize')}</span>
                <div className="settings-toggle-group">
                  <button
                    className={`settings-toggle-btn settings-font-s${settings.fontSize === 'small' ? ' active' : ''}`}
                    onClick={() => updateSetting('fontSize', 'small')}
                    type="button"
                  >
                    A
                  </button>
                  <button
                    className={`settings-toggle-btn settings-font-m${settings.fontSize === 'normal' ? ' active' : ''}`}
                    onClick={() => updateSetting('fontSize', 'normal')}
                    type="button"
                  >
                    A
                  </button>
                  <button
                    className={`settings-toggle-btn settings-font-l${settings.fontSize === 'large' ? ' active' : ''}`}
                    onClick={() => updateSetting('fontSize', 'large')}
                    type="button"
                  >
                    A
                  </button>
                </div>
              </div>
              <div className="settings-row">
                <span className="settings-label">{t('language')}</span>
                <div className="settings-toggle-group">
                  <button
                    className={`settings-toggle-btn${settings.language === 'en' ? ' active' : ''}`}
                    onClick={() => updateSetting('language', 'en')}
                    type="button"
                  >
                    EN
                  </button>
                  <button
                    className={`settings-toggle-btn${settings.language === 'es' ? ' active' : ''}`}
                    onClick={() => updateSetting('language', 'es')}
                    type="button"
                  >
                    ES
                  </button>
                </div>
              </div>
              {ttsSupported && tier !== 'basico' && (
                <div className="settings-row">
                  <span className="settings-label">{t('readAloud')}</span>
                  <div className="settings-toggle-group">
                    <button
                      className={`settings-toggle-btn${settings.readAloud ? ' active' : ''}`}
                      onClick={() => updateSetting('readAloud', true)}
                      type="button"
                    >
                      {t('on')}
                    </button>
                    <button
                      className={`settings-toggle-btn${!settings.readAloud ? ' active' : ''}`}
                      onClick={() => updateSetting('readAloud', false)}
                      type="button"
                    >
                      {t('off')}
                    </button>
                  </div>
                </div>
              )}
              {ttsSupported && tier !== 'basico' && (
                <div className="settings-row">
                  <span className="settings-label">{t('voiceLabel')}</span>
                  <div className="settings-select-wrapper" ref={voiceMenuRef}>
                    <button
                      type="button"
                      className="settings-select-btn"
                      onClick={() => setVoiceMenuOpen((open) => !open)}
                      aria-haspopup="listbox"
                      aria-expanded={voiceMenuOpen}
                    >
                      <span>{selectedVoiceName}</span>
                      {Icons.chevronDown}
                    </button>
                    {voiceMenuOpen && (
                      <div className="settings-select-menu" role="listbox">
                        <button
                          type="button"
                          role="option"
                          aria-selected={settings.voiceURI === ''}
                          className={`settings-select-item${settings.voiceURI === '' ? ' active' : ''}`}
                          onClick={() => { updateSetting('voiceURI', ''); setVoiceMenuOpen(false); }}
                        >
                          {t('voiceAuto')}
                        </button>
                        {(CURATED_VOICES[settings.language] || CURATED_VOICES.en).map((voice) => (
                          <button
                            type="button"
                            role="option"
                            aria-selected={settings.voiceURI === voice.voiceURI}
                            key={voice.voiceURI}
                            className={`settings-select-item${settings.voiceURI === voice.voiceURI ? ' active' : ''}`}
                            onClick={() => { updateSetting('voiceURI', voice.voiceURI); setVoiceMenuOpen(false); }}
                          >
                            {voice.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div className="settings-row">
                <span className="settings-label">{t('resetSettings')}</span>
                <button className="settings-toggle-btn" type="button" onClick={handleResetSettings}>
                  {t('reset')}
                </button>
              </div>
            </div>

            <div className="settings-section">
              <h3>{t('chatSection')}</h3>
              <div className="settings-row">
                <span className="settings-label">{t('exportAllConversations')}</span>
                <div className="settings-toggle-group">
                  <button className="settings-toggle-btn" type="button" onClick={() => handleExportAll('markdown')}>
                    Markdown
                  </button>
                  <button className="settings-toggle-btn" type="button" onClick={() => handleExportAll('json')}>
                    JSON
                  </button>
                </div>
              </div>
              <div className="settings-row">
                <span className="settings-label">{t('exportCurrentConversation')}</span>
                <button className="settings-toggle-btn" type="button" onClick={handleExportCurrent}>
                  Markdown
                </button>
              </div>
              <div className="settings-row">
                <span className="settings-label">{t('deleteAllHistory')}</span>
                <button
                  className="settings-danger-btn"
                  type="button"
                  onClick={handleClearAllHistory}
                >
                  {t('delete')}
                </button>
              </div>
            </div>

            <RemotePinSettings user={user} t={t} />

            <ApiKeysSettings user={user} t={t} />

            {user && (
              <div className="settings-section">
                <h3>{t('accountTitle')}</h3>
                <div className="settings-row">
                  <span className="settings-label">{user.email || t('authenticatedUser')}</span>
                  <button
                    className="settings-danger-btn"
                    type="button"
                    onClick={() => {
                      setShowSettings(false);
                      handleSignOut();
                    }}
                  >
                    {t('signOut')}
                  </button>
                </div>
              </div>
            )}

            <div className="settings-section">
              <h3>{t('subscriptionSection')}</h3>
              <div className="settings-row">
                <span className="settings-label">{t('managePlan')}</span>
                <button
                  className="settings-action-btn"
                  type="button"
                  onClick={() => {
                    setShowSettings(false);
                    navigate('/subscribe');
                  }}
                >
                  {t('view')}
                </button>
              </div>
            </div>

            <div className="settings-section settings-about">
              <h3>{t('about')}</h3>
              <p>wheniseeus <strong>v1.0</strong></p>
              <p style={{ color: '#78716c', fontSize: '13px', marginTop: '4px' }}>{t('poweredBy')}</p>
            </div>
          </div>
        </div>
      )}

      {showAuthModal && (
        <div className="modal-overlay" onClick={handleCloseAuthModal}>
          <div className="modal auth-modal" onClick={(e) => e.stopPropagation()}>
            <h2 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {Icons.user} {t('accountTitle')}
            </h2>

            {user ? (
              <div className="user-summary">
                <p>{t('signedInAs')}</p>
                <strong>{user.email || t('authenticatedUser')}</strong>
                <button
                  className="modal-button primary"
                  onClick={handleSignOut}
                >
                  {t('signOut')}
                </button>
              </div>
            ) : (
              <>
                <div className="auth-tabs">
                  <button
                    type="button"
                    className={`auth-tab ${authMode === 'login' ? 'active' : ''}`}
                    onClick={() => {
                      setAuthMode('login');
                      setAuthError('');
                    }}
                  >
                    Login
                  </button>
                  <button
                    type="button"
                    className={`auth-tab ${authMode === 'register' ? 'active' : ''}`}
                    onClick={() => {
                      setAuthMode('register');
                      setAuthError('');
                    }}
                  >
                    Register
                  </button>
                </div>

                <form className="auth-form" onSubmit={handleSubmitAuth}>
                  <input
                    className="auth-input"
                    type="email"
                    name="email"
                    placeholder="Email"
                    value={authForm.email}
                    onChange={handleAuthFieldChange}
                    disabled={authLoading}
                  />
                  <input
                    className="auth-input"
                    type="password"
                    name="password"
                    placeholder="Password"
                    value={authForm.password}
                    onChange={handleAuthFieldChange}
                    disabled={authLoading}
                  />

                  {authError && <p className="auth-error">{authError}</p>}

                  <button className="modal-button primary" type="submit" disabled={authLoading}>
                    {authLoading ? 'Processing...' : authMode === 'login' ? 'Log in' : 'Create account'}
                  </button>
                </form>

                <div className="auth-or" role="separator"><span>{t('orDivider')}</span></div>
                <button className="auth-google-btn" type="button" onClick={handleGoogleAuth} disabled={authLoading}>
                  <img className="auth-google-logo" src="/google-g-logo.svg" alt="" width="18" height="18" />
                  <span>{t('continueWithGoogle')}</span>
                </button>
              </>
            )}

            <button
              className="modal-button secondary"
              onClick={handleCloseAuthModal}
            >
              {t('close')}
            </button>
          </div>
        </div>
      )}

      {showSupport && (
        <div className="modal-overlay" onClick={() => setShowSupport(false)}>
          <div className="modal support-modal" onClick={(e) => e.stopPropagation()}>
            <h2 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {Icons.support} {t('menuSupport')}
            </h2>
            <p className="support-intro">
              {t('supportIntro')}
            </p>

            {supportSent ? (
              <p className="auth-success">{t('supportSentMsg')}</p>
            ) : (
              <form className="auth-form" onSubmit={handleSubmitSupport}>
                <input
                  className="auth-input"
                  type="text"
                  name="nombre"
                  placeholder={t('namePlaceholder')}
                  value={supportForm.nombre}
                  onChange={handleSupportFieldChange}
                  disabled={supportSending}
                />
                <input
                  className="auth-input"
                  type="email"
                  name="email"
                  placeholder={t('emailPlaceholder')}
                  value={supportForm.email}
                  onChange={handleSupportFieldChange}
                  disabled={supportSending}
                />
                <textarea
                  className="auth-input support-textarea"
                  name="mensaje"
                  placeholder={t('messagePlaceholder')}
                  value={supportForm.mensaje}
                  onChange={handleSupportFieldChange}
                  disabled={supportSending}
                  rows={4}
                />
                {supportError && <p className="auth-error">{supportError}</p>}
                <button className="modal-button primary" type="submit" disabled={supportSending}>
                  {supportSending ? t('sending') : t('sendMessage')}
                </button>
              </form>
            )}

            <div className="auth-footer-links support-links">
              <a href="/terminos.html" target="_blank" rel="noreferrer">{t('termsAndConditions')}</a>
              <a href="/privacidad.html" target="_blank" rel="noreferrer">{t('privacyPolicy')}</a>
              <a href="/reembolsos.html" target="_blank" rel="noreferrer">{t('refundPolicy')}</a>
            </div>
            <button
              className="modal-button secondary"
              onClick={() => setShowSupport(false)}
            >
              {t('close')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
